import axios from 'axios';
import { ActorUpdateSettings, AdminUserFilters, AdminWorkspaceFilters, AudioVoiceSettings, AvatarGroup, AvatarGroupCreationRequest, CreateImageToVideoRequest, CreateProImageToVideoRequest, CreateProTextToVideoRequest, CreateSeedance15ProRequest, CreateSeedance2Request, CreateSeedance2FastRequest, CreateSeedance25Request, CreateSeedanceImageToVideoRequest, CreateSeedanceTextToVideoRequest, CreateGeminiGenerateRequest, GeminiResponse, GeminiState, GeminiTaskResponse, GeminiTasksResponse, CreateWan3GenerateRequest, Wan3Response, Wan3State, Wan3TaskResponse, Wan3TasksResponse, CreateHetznerGenerateRequest, HetznerResponse, HetznerState, HetznerTaskResponse, HetznerTasksResponse, CreatePromptSuggestionRequest, CreatePromptSuggestionResponse, PromptSuggestionResultResponse, CreateCustomPhotoAvatarV3UploadResponse, CreateCustomPhotoAvatarV3PromptRequest, UpdateAvatarLookV3Response, ListAvatarGroupsV3Response, GetAvatarGroupV3Response, CreateStoryboardRequest, CreateTextToVideoRequest, CreateVeo3GenerateRequest, CreateVeo3ExtendRequest, Veo3FourKRequest, CustomPhotoAvatar, CustomVoice, ElevenLabsVoice, GenerateCustomPhotoAvatarRequest, HeygenVoice, GenerateIVAvatarVideoRequest, IVAvatarVideo, LookGenerationRequest, Script, SelectPhotoAvatarImageRequest, SeedanceResponse, SeedanceState, SeedanceTaskResponse, SeedanceTasksResponse, CreateNanoBananaRequest, NanoBananaResponse, NanoBananaState, NanoBananaTaskResponse, NanoBananaTasksResponse, FeedbackLoopMedia, SilenceVoiceSettings, Sora2Response, Sora2State, Sora2TaskResponse, Sora2TasksResponse, Veo3Response, Veo3ExtendResponse, Veo31080pResponse, Veo3FourKResponse, Veo3State, Veo3TaskResponse, Veo3TasksResponse, SaveToAssetsRequest, SaveToAssetsResponse, TextVoiceSettings, UpdateCustomPhotoAvatarVoiceSettingsRequest, User, VideoPage, VoiceLocale } from '../context/types';
import { delay, formatDate } from '../utils/utils';
import Cookies from 'js-cookie';
import { setSsoCookie, clearSsoCookie } from '../utils/ssoCookie';

const url = process.env.REACT_APP_API_URL;
const apiKey = process.env.REACT_APP_API_KEY;

const api = axios.create({
  baseURL: url,
  headers: {
    'Access-Control-Allow-Origin': '*',
    'Content-Type': 'application/json',
    'X-API-Key': apiKey,
  },
});
api.interceptors.request.use((config) => {
  const authInfo = Cookies.get('authInfo');
  if (authInfo) {
    config.headers['X-Auth-Info'] = authInfo;
  }
  if (!config.headers['Authorization']) {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
  }
  return config;
});

// Access tokens are now short-lived (Cognito, ~1hr default) rather than
// the old 7-day JWT, so a silent-refresh path is required, not optional.
// No response interceptor existed here before this - expired tokens used
// to just fail silently per-component with no recovery.
const REFRESH_EXCLUDED_PATHS = ['/api/auth/login', '/api/auth/refresh-token', '/api/auth/mfa/'];

// Shared across concurrent 401s so they all await the same refresh call
// instead of each firing their own (wasted calls, rate-limit risk, and a
// write race on which response lands in localStorage last).
let refreshPromise: Promise<string | null> | null = null;

export const performTokenRefresh = async (): Promise<string | null> => {
  const storedRefreshToken = localStorage.getItem('refreshToken');
  if (!storedRefreshToken) return null;

  try {
    const response = await axios.post(
      `${url}/api/auth/refresh-token`,
      { refreshToken: storedRefreshToken },
      { headers: { 'Content-Type': 'application/json', 'X-API-Key': apiKey } }
    );
    const newToken = response.data?.token;
    if (!newToken) return null;
    localStorage.setItem('token', newToken);
    setSsoCookie(newToken); // keep zapflow/farm-manager's shared cookie fresh too
    return newToken;
  } catch {
    return null;
  }
};

const clearSessionAndRedirectToLogin = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userId');
  Cookies.remove('authInfo', {
    domain: process.env.REACT_APP_COOKIE_DOMAIN,
    path: '/',
  });
  clearSsoCookie();
  // Assigning location.href to the page you're already on still forces a
  // full reload - if a stale pre-Cognito session's 401 fires from a call
  // made ON the login page itself (e.g. checkAdminStatus, triggered as
  // soon as checkUserFromCookie finds isAppAdmin true), that reload just
  // re-runs the same code and 401s again, forever. Only navigate if we're
  // not already on one of the pages where "not logged in" is the expected,
  // harmless default - /signup included, since a stale/expired session in
  // the same browser used to bounce invite links straight back to /login
  // before a new account was ever created.
  const PUBLIC_AUTH_PATHS = ['/login', '/signup', '/mfa'];
  if (!PUBLIC_AUTH_PATHS.includes(window.location.pathname)) {
    window.location.href = '/login';
  }
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const requestPath: string = originalRequest?.url || '';
    const isExcluded = REFRESH_EXCLUDED_PATHS.some((path) => requestPath.includes(path));

    if (error.response?.status !== 401 || isExcluded || !originalRequest || originalRequest._retry) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (!refreshPromise) {
      refreshPromise = performTokenRefresh().finally(() => {
        refreshPromise = null;
      });
    }

    const newToken = await refreshPromise;

    if (!newToken) {
      clearSessionAndRedirectToLogin();
      return Promise.reject(error);
    }

    originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
    return api(originalRequest);
  }
);

export default api;

// Actors API
export const getActors = async (organizationId: string, workspaceId: string) => {
  const params = new URLSearchParams();
  if (organizationId) params.append('organizationId', organizationId);
  if (workspaceId) params.append('workspaceId', workspaceId);
  
  const response = await api.get(`/api/actors?${params.toString()}`);
  return response.data;
};

export const getInternalActors = async () => {
  const response = await api.get('/api/actors/internal');
  return response.data;
};

export const registerActor = async (actorData: object) => {
  const response = await api.post('/api/actors', actorData);
  return response.data;
};

export const deleteActor = async (id: string, workspaceId: string, videoPageId: string) => {
  const response = await api.delete(`/api/actors/${workspaceId}/${videoPageId}/${id}`);
  return response.data;
};

// Fetch actor by avatar ID
export const getActorByAvatarId = async (avatarId: string, workspaceId: string, videoPageId: string) => {
  const response = await api.get(`/api/actors/avatar/${workspaceId}/${videoPageId}/${avatarId}`);
  return response.data;
};



// Generated Video API
export const addGeneratedVideo = async (videoData: object) => {
  const response = await api.post('/api/generated-videos', videoData);
  return response.data;
};

export const updateGeneratedVideo = async (id: string, videoData: object) => {
  const response = await api.put(`/api/generated-videos/${id}`, videoData);
  return response.data;
};

export const deleteGeneratedVideo = async (id: string) => {
  const response = await api.delete(`/api/generated-videos/${id}`);
  return response.data;
};

// Video Agent API
export const generateVideoAgent = async (data: {
  prompt: string;
  workspace: string;
  generatedBy: string;
  name?: string;
  avatar_id?: string;
  voice_id?: string;
  style_id?: string;
  brand_kit_id?: string;
  orientation?: 'portrait' | 'landscape';
  aspect_ratio?: string;
  duration_sec?: number;
  files?: Array<Record<string, unknown>>;
  incognito_mode?: boolean;
  config?: {
    avatar_id?: string;
    duration_sec?: number;
    orientation?: 'portrait' | 'landscape';
  };
}) => {
  const response = await api.post('/api/generated-videos/video-agent', data);
  return response.data;
};

export const getVideoAgentVideos = async (workspaceId: string) => {
  const response = await api.get(`/api/generated-videos/video-agent/workspace/${workspaceId}`);
  return response.data;
};

export const createVideoAgentSession = async (data: {
  prompt: string;
  mode?: 'generate' | 'chat';
  workspace: string;
  generatedBy?: string;
  name?: string;
  callbackId?: string;
  callbackUrl?: string;
  avatar_id?: string;
  voice_id?: string;
  style_id?: string;
  brand_kit_id?: string;
  orientation?: 'portrait' | 'landscape';
  aspect_ratio?: string;
  files?: Array<Record<string, unknown>>;
  incognito_mode?: boolean;
}) => {
  const response = await api.post('/api/video-agent-sessions', data);
  return response.data;
};

export const getVideoAgentSession = async (sessionId: string) => {
  const response = await api.get(`/api/video-agent-sessions/${sessionId}`);
  return response.data;
};

export const postVideoAgentSessionMessage = async (sessionId: string, data: {
  message: string;
  files?: Array<Record<string, unknown>>;
  avatar_id?: string;
  voice_id?: string;
  brand_kit_id?: string;
}) => {
  const response = await api.post(`/api/video-agent-sessions/${sessionId}/messages`, data);
  return response.data;
};

export const getVideoAgentSessionStyles = async (
  tag?: string,
  limit: number = 20,
  token?: string
) => {
  const params = new URLSearchParams();
  if (tag) params.append('tag', tag);
  if (limit) params.append('limit', String(limit));
  if (token) params.append('token', token);
  const response = await api.get(`/api/video-agent-sessions/styles?${params.toString()}`);
  return response.data;
};

// Asset Actor API
export const createAssetActor = async (data: {
  name: string;
  avatar_id?: string;
  image_url?: string;
  image_asset_id?: string;
  voice_id?: string;
  voiceName?: string;
  voice_settings?: { speed?: number; pitch?: number; locale?: string };
  audio_url?: string;
  audio_asset_id?: string;
  resolution?: '4k' | '1080p' | '720p';
  aspect_ratio?: '16:9' | '9:16';
  motion_prompt?: string;
  expressiveness?: string;
  remove_background?: boolean;
  background?: { type: string; value?: string; url?: string; asset_id?: string };
  workspace: string;
  videoPageId: string;
  createdBy: string;
}) => {
  const response = await api.post('/api/asset-actors', data);
  return response.data;
};

export const updateAssetActor = async (id: string, data: object) => {
  const response = await api.put(`/api/asset-actors/${id}`, data);
  return response.data;
};

export const deleteAssetActor = async (id: string) => {
  const response = await api.delete(`/api/asset-actors/${id}`);
  return response.data;
};

export const getAssetActorsByVideoPage = async (videoPageId: string) => {
  const response = await api.get(`/api/asset-actors/video-page/${videoPageId}`);
  return response.data;
};

// V2 Video Generation (Image Animation) — lives under the video page
export const generateVideoV2 = async (data: {
  videoPageId: string;
  workspaceId: string;
  assetActorId: string;
  userEmail: string;
  scriptId?: string;
  script?: {
    headline?: string;
    body?: string;
    cta?: string;
  };
  resolution?: '4k' | '1080p' | '720p';
  aspect_ratio?: '16:9' | '9:16';
}) => {
  const { videoPageId, ...body } = data;
  const response = await api.post(`/api/videopages/${videoPageId}/generate-video-v2`, body);
  return response.data;
};

// Project API
export const createProject = async (name: string, workspaceId: string) => {
  const response = await api.post('/api/projects', { name, workspaceId });
  return response.data;
};

export const updateProject = async (id: string, projectData: object) => {
  const response = await api.put(`/api/projects/${id}`, projectData);
  return response.data;
};

export const deleteProject = async (id: string) => {
  const response = await api.delete(`/api/projects/${id}`);
  return response.data;
};

// NEW: Get project by ID
export const getProjectById = async (id: string) => {
  const response = await api.get(`/api/projects/${id}`);
  return response.data;
};

// NEW: Get all projects with pagination (default 10 results)
export const getAllProjects = async (
  page: number = 1,
  limit: number = 10,
  search: string = '',
  workspaceId: string,
) => {
  const response = await api.get(`/api/projects/workspace/${workspaceId}?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`);
  return response.data;
};

export const addVideoPageToProject = async (id: string, name: string, userId = '') => {
  const response = await api.post(`/api/projects/${id}/videopages`, {name, userId});
  return response.data;
};

export const removeVideoPageFromProject = async (id: string, videoPageId: string) => {
  const response = await api.delete(`/api/projects/${id}/videopages/${videoPageId}`);
  return response.data;
};

// VideoPage API
export const createVideoPage = async (videoPageData: object, userId: string) => {
  const response = await api.post('/api/videopages', { videoPageData, userId}) ;
  return response.data;
};

export const updateVideoPage = async (id: string, videoPageData: object, userEmail: string) => {
  const response = await api.put(`/api/videopages/${id}`, {videoPageData, userEmail} );
  return response.data;
};

export const deleteVideoPage = async (id: string) => {
  const response = await api.delete(`/api/videopages/${id}`);
  return response.data;
};

export const getVideoPageById = async (id: string) => {
  const response = await api.get(`/api/videopages/${id}`);
  return response.data;
};

export const generateVideo = async (
  workspaceId: string,
  id: string,
  actorId: string,
  voiceId: string,
  script: string,
  scriptId: string,
  dimension: { width: number; height: number },
  userEmail: string,
  videoPageId: string,
  options?: { captionStyle?: string; captionExportType?: 'baked_in' | 'separate' | 'both'; captionTheme?: Record<string, unknown>; resolution?: '4k' | '1080p' | '720p' }
) => {
  const response = await api.post(`/api/videopages/${id}/generate-video`, {
    workspaceId,
    actorId,
    voiceId,
    script,
    scriptId,
    dimension,
    userEmail,
    videoPageId,
    ...(options?.captionStyle !== undefined && { captionStyle: options.captionStyle }),
    ...(options?.captionExportType !== undefined && { captionExportType: options.captionExportType }),
    ...(options?.captionTheme !== undefined && { captionTheme: options.captionTheme }),
    ...(options?.resolution !== undefined && { resolution: options.resolution }),
  });
  return response.data;
};

//generate multiple videos using 1 script and multiple actors
export const generateVideosMultipleActors = async (
  workspaceId: string,
  id: string,
  actorIds: string[],
  voiceIds: Record<string, string>,
  script: Script,
  scriptId: string,
  dimension: { width: number; height: number },
  userEmail: string,
  videoPageId: string,
  isSpeech: boolean,
  options?: { captionStyle?: string; captionExportType?: 'baked_in' | 'separate' | 'both'; captionTheme?: Record<string, unknown>; resolution?: '4k' | '1080p' | '720p' }
) => {
  const responses = await Promise.all(
    actorIds.map((actorId) =>
      api.post(`/api/videopages/${id}/generate-video`, {
        workspaceId,
        actorId,
        voiceId: voiceIds[actorId],
        script,
        scriptId,
        dimension,
        userEmail,
        videoPageId,
        isSpeech,
        ...(options?.captionStyle !== undefined && { captionStyle: options.captionStyle }),
        ...(options?.captionExportType !== undefined && { captionExportType: options.captionExportType }),
        ...(options?.captionTheme !== undefined && { captionTheme: options.captionTheme }),
        ...(options?.resolution !== undefined && { resolution: options.resolution }),
      })
    )
  );
  return responses.map((response) => response.data);
};

export const generateVideosMultipleScripts = async (
  workspaceId: string,
  videoPageId: string,
  actorId: string,
  voiceId: string,
  scripts: Script[],
  dimension: { width: number; height: number },
  userEmail: string,
  options?: { captionStyle?: string; captionExportType?: 'baked_in' | 'separate' | 'both'; captionTheme?: Record<string, unknown>; resolution?: '4k' | '1080p' | '720p' }
) => {
  try {
    const response = await api.post(`/api/videopages/${videoPageId}/generate-video/scripts`, {
      workspaceId,
      actorId,
      voiceId,
      scripts,
      dimension,
      userEmail,
      videoPageId,
      ...(options?.captionStyle !== undefined && { captionStyle: options.captionStyle }),
      ...(options?.captionExportType !== undefined && { captionExportType: options.captionExportType }),
      ...(options?.captionTheme !== undefined && { captionTheme: options.captionTheme }),
      ...(options?.resolution !== undefined && { resolution: options.resolution }),
    });
    return response.data.videoIds;
  } catch (error) {
    console.error("Error generating videos:", error);
    throw error;
  }
};

export const removeGeneratedVideo = async (videoPageId: string, videoId: string) => {
  const response = await api.delete(`/api/videopages/${videoPageId}/generated-videos/${videoId}`);
  return response.data;
};

export const updateGeneratedVideoStatus = async (videoId: string) => {
  const response = await api.put(`/api/generated-videos/${videoId}/status`);
  return response.data;
};

export const updateScript = async (id: string, scriptData: object, userEmail: string) => {
  const response = await api.put(`/api/videopages/${id}/script`, {...scriptData, userEmail});
  return response.data;
};

// VideoPage API - Actors
export const updateVideoPageActors = async (id: string, actorIds: string[] | undefined, userEmail: string) => {
  const response = await api.put(`/api/videopages/${id}/actors`, { actorIds, userEmail });
  return response.data;
};

export const removeVideoPageActor = async (id: string, actorId: string) => {
  const response = await api.delete(`/api/videopages/${id}/actors`, { data: { actorId } });
  return response.data;
};

export const deleteScript = async (
  videoPageId: string,
  scriptId: string,
  userEmail: string
) => {
  const response = await api.post(`/api/videopages/${videoPageId}/delete-script`, { 
    scriptId, 
    userEmail 
  });
  return response.data;
};

// Voice API
export const addVoice = async (voiceData: object) => {
  const response = await api.post('/api/voices', voiceData);
  return response.data;
};

export const updateVoice = async (id: string, voiceData: object) => {
  const response = await api.put(`/api/voices/${id}`, voiceData);
  return response.data;
};

export const deleteVoice = async (id: string) => {
  const response = await api.delete(`/api/voices/${id}`);
  return response.data;
};

export const uploadSpeechAudio = async (
  workspaceId: string,
  projectId: string,
  videoPageId: string,
  scriptName: string,
  audioFile: File
): Promise<string> => {
  try {
    // Create FormData to send the file
    const formData = new FormData();
    formData.append('audio', audioFile);

    // Send the request to the API
    const response = await api.post(
      `/api/videopages/${workspaceId}/${projectId}/${videoPageId}/${scriptName}/upload-speech-audio`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data', // Let axios set the boundary automatically
        },
      }
    );

    // Return the S3 URL from the response
    return response.data.speechAudioUrl;
  } catch (error) {
    console.error('Error uploading speech audio:', error);
    throw error;
  }
};

// Delete speech audio
export const removeSpeechAudio = async (videoPageId: string) => {
  try {
    const response = await api.delete(
      `/api/videopages/speech/${videoPageId}`
    );

    return response;
  } catch (error) {
    console.error('Error uploading speech audio:', error);
    throw error;
  }
};


// Authentication API
export const registerUser = async (name: string, email: string, password: string, role: string, organizationKey: string, redirect?: string | null/*, accessKey: string*/) => {
  const response = await api.post('/api/auth/signup', { name, email, password, role, organizationKey, redirect/*, accessKey*/ });
  return response.data;
};

export const loginUser = async (email: string, password: string) => {
  const response = await api.post('/api/auth/login', { email, password });
  return response.data;
};

export interface MfaLoginResult {
  user: any;
  token: string;
  refreshToken?: string;
  expiresIn?: number;
  recoveryCodes?: string[];
}

export const mfaVerify = async (
  ticket: string,
  input: { code?: string; recoveryCode?: string }
): Promise<MfaLoginResult> => {
  const response = await api.post('/api/auth/mfa/verify', { ticket, ...input });
  return response.data;
};

export const mfaSetupStart = async (ticket: string): Promise<{ qrDataUrl: string; manualKey: string }> => {
  const response = await api.post('/api/auth/mfa/setup/start', { ticket });
  return response.data;
};

export const mfaSetupConfirm = async (ticket: string, code: string): Promise<MfaLoginResult> => {
  const response = await api.post('/api/auth/mfa/setup/confirm', { ticket, code });
  return response.data;
};

export const getUserById = async (id: string) => {
  const response = await api.get(`/api/auth/user/${id}`);
  return response.data;
};

export const getUsersByOrganization = async (organizationId: string) => {
  try {
    const response = await api.get(`/api/auth/organization/${organizationId}/users`);
    return response.data.users;
  } catch (error: any) {
    throw new Error(`Error fetching users by organization: ${error.response?.data?.message || error.message}`);
  }
};

// Password reset API methods
export const requestPasswordReset = async (email: string): Promise<{ message: string }> => {
  try {
    const response = await api.post('/api/auth/request-password-reset', { email });
    return response.data;
  } catch (error: any) {
    console.error('Password reset request failed:', error);
    throw new Error(error.response?.data?.message || 'Failed to request password reset');
  }
};

export const resetPassword = async (token: string, newPassword: string): Promise<{ message: string }> => {
  try {
    const response = await api.post('/api/auth/reset-password', { token, newPassword });
    return response.data;
  } catch (error: any) {
    console.error('Password reset failed:', error);
    throw new Error(error.response?.data?.message || 'Failed to reset password');
  }
};

export const validateResetToken = async (token: string): Promise<{ valid: boolean }> => {
  try {
    const response = await api.get(`/api/auth/validate-reset-token/${token}`);
    return response.data;
  } catch (error: any) {
    console.error('Token validation failed:', error);
    throw new Error(error.response?.data?.message || 'Failed to validate token');
  }
};

export const testEmailService = async (email: string): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    const response = await api.post('/api/auth/test-email', { email });
    return response.data;
  } catch (error: any) {
    console.error('Email test failed:', error);
    throw new Error(error.response?.data?.message || 'Failed to test email service');
  }
};

// Email verification API methods
export const verifyEmail = async (token: string): Promise<{ message: string }> => {
  try {
    const response = await api.post('/api/auth/verify-email', { token });
    return response.data;
  } catch (error: any) {
    console.error('Email verification failed:', error);
    throw new Error(error.response?.data?.message || 'Failed to verify email');
  }
};

export const resendVerificationEmail = async (email: string): Promise<{ message: string }> => {
  try {
    const response = await api.post('/api/auth/resend-verification', { email });
    return response.data;
  } catch (error: any) {
    console.error('Resend verification failed:', error);
    throw new Error(error.response?.data?.message || 'Failed to resend verification email');
  }
};

// Google OAuth methods
export const initiateGoogleSignIn = (): void => {
  // Redirect to Google OAuth endpoint
  window.location.href = `${process.env.REACT_APP_API_URL}/api/auth/google`;
};

export const completeGoogleProfile = async (organizationKey: string, token: string): Promise<{ token: string; refreshToken?: string; expiresIn?: number; user: any }> => {
  const response = await api.post('/api/auth/google/complete-profile',
    { organizationKey },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return response.data;
};

export const getGoogleUserProfile = async (): Promise<{ user: any; requiresProfileCompletion: boolean }> => {
  const response = await api.get('/api/auth/google/profile');
  return response.data;
};

// Helper to check if we're in the Google callback flow
export const isGoogleAuthCallback = (): boolean => {
  return window.location.pathname === '/auth/success' || 
         window.location.pathname === '/complete-profile';
};

// Helper to extract token from URL (for OAuth callback)
export const getTokenFromUrl = (): string | null => {
  const params = new URLSearchParams(window.location.search);
  return params.get('token');
};

export const getUserByToken = async (token: string): Promise<any> => {
  const response = await api.get('/api/auth/user-from-token', {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
  return response.data;
};

// Workspace API
export const createWorkspace = async (name: string, userId: string) => {
  const response = await api.post('/api/workspaces', { name, owner: userId });
  return response.data;
};

export const updateWorkspace = async (id: string, workspaceData: object) => {
  const response = await api.put(`/api/workspaces/${id}`, workspaceData);
  return response.data;
};

export const deleteWorkspace = async (id: string) => {
  const response = await api.delete(`/api/workspaces/${id}`);
  return response.data;
};

export const addMemberToWorkspace = async (workspaceId: string, userId: string) => {
  const response = await api.post(`/api/workspaces/${workspaceId}/members`, { memberId: userId });
  return response.data;
};

export const removeMemberFromWorkspace = async (workspaceId: string, userId: string) => {
  const response = await api.delete(`/api/workspaces/${workspaceId}/members/${userId}`);
  return response.data;
};

export const addAdminToWorkspace = async (workspaceId: string, userId: string) => {
  const response = await api.post(`/api/workspaces/${workspaceId}/admins`, { adminId: userId });
  return response.data;
};

export const removeAdminFromWorkspace = async (workspaceId: string, adminId: string) => {
  const response = await api.delete(`/api/workspaces/${workspaceId}/admins/${adminId}`);
  return response.data;
};

export const setWorkspaceInvitation = async (workspaceId: string, role: string) => {
  const response = await api.post(`/api/workspaces/${workspaceId}/invitation`, { role });
  return response.data;
};

export const checkIfInvitationIsValid = async (workspaceId: string, token: string) => {
  const response = await api.get(`/api/workspaces/${workspaceId}/invitation/validate/${token}`);
  return response.data;
};

export const getWorkspaceById = async (id: string) => {
  const response = await api.get(`/api/workspaces/${id}`);
  return response.data;
};

export const addInvitation = async (workspaceId: string, role: string, userId: string, email: string) => {
  const response = await api.post(`/api/workspaces/${workspaceId}/invitations`, { role, userId, email });
  return response.data;
};

export const acceptInvitation = async (workspaceId: string, encodedKey: string, userId: string) => {
  const response = await api.post(`/api/workspaces/${workspaceId}/invitation/accept/${encodedKey}`, { userId });
  return response.data;
};

export const setInvitations = async (workspaceId: string, invitations: Array<{ email: string, role: string }>) => {
  const response = await api.put(`/api/workspaces/${workspaceId}/invitations`, { invitations });
  return response.data;
};

export const giveUserAppFullAccess = async (userId: string, accessKey: string) => {
  const response = await api.post('/api/auth/give-full-access', { userId, accessKey });
  return response.data;
};

// Check LetsLaunch access by organization key
export const checkLetsLaunchAccess = async (organizationKey: string) => {
  try {
    const response = await api.get(`/api/auth/letslaunch/access/${organizationKey}`);
    return response.data.hasAccess;
  } catch (error: any) {
    throw new Error(`Error checking LetsLaunch access: ${error.response?.data?.message || error.message}`);
  }
};

// Get organization details by organization key
export const getOrganizationDetails = async (organizationKey: string) => {
  try {
    const response = await api.post('/organization', { organizationKey });
    return response.data;
  } catch (error: any) {
    throw new Error(`Error fetching organization details: ${error.response?.data?.message || error.message}`);
  }
};

// OpenAI API - Text Generation 
export const getSuggestion = async (prompt: string, maxTokens: number) => {
  try {
    const response = await api.post('/api/openai/suggestion', { prompt, maxTokens });
    return response.data;
  } catch (error) {
    console.error("Error fetching headline suggestion:", error);
    throw error;
  }
};

// Voice Customization API

// Add voice to an actor
export const addVoiceToActor = async (actorId: string, voiceId: string, voiceName: string, workspaceId: string, videoPageId: string, userEmail:string) => {
  const response = await api.post(`/api/custom-voices/${workspaceId}/${userEmail}/${actorId}/voice`, { voiceId, voiceName, videoPageId });
  return response.data;
};

// Update voice configuration for an actor
export const updateVoiceConfiguration = async (actorId: string, voiceConfig: object, userEmail: string) => {
  const response = await api.put(`/api/custom-voices/${actorId}/voice-configuration/${userEmail}`, { newSettings: voiceConfig });
  return response.data;
};

// Get voice configuration for an actor
export const getVoiceConfiguration = async (actorId: string, userEmail: string) => {
  const response = await api.get(`/api/custom-voices/${actorId}/voice-configuration/${userEmail}`);
  return response.data;
};


// Get eleven labs voices
export const getElevenLabsVoices = async (userEmail: string): Promise<ElevenLabsVoice[]> => {
    try {
        const response = await api.get(`/api/custom-voices/all/${userEmail}`);
        return response.data.voices;
    } catch (error) {
        console.error('Error fetching voices:', error);
        throw error;
    }
};

// Get eleven labs voices
export const getElevenLabsVoice = async (voiceId: string, userEmail: string): Promise<ElevenLabsVoice> => {
    try {
        const response = await api.get(`/api/custom-voices/${voiceId}/${userEmail}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching voices:', error);
        throw error;
    }
};

// Remove eleven labs voice data from actor 
export const removeActorCustomVoice = async (actorId: string, workspaceId: string, videoPageId: string) => {
    try {
        const response = await api.delete(`/api/custom-voices/${workspaceId}/${videoPageId}/${actorId}`);
        return response;
    } catch (error) {
        console.error('Error fetching voices:', error);
        throw error;
    }
};

//Speech to Speech
export const speechToSpeech = async (
  voiceId: string,
  audioFile: File,
  outputFormat: string = "mp3_44100_128",
  modelId: string = "eleven_multilingual_sts_v2"
): Promise<Blob> => {
  try {
    // Create FormData to send the file
    const formData = new FormData();
    formData.append('audio', audioFile);
    formData.append('outputFormat', outputFormat);
    formData.append('modelId', modelId);

    // Send the request to the API
    const response = await api.post(`/api/custom-voices/speech-to-speech/${voiceId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data', // Required for file uploads
      },
      responseType: 'blob', // To handle binary data (audio file)
    });

    // Return the converted audio as a Blob
    return response.data;
  } catch (error) {
    console.error('Error in speechToSpeech API call:', error);
    throw error;
  }
};


// Heygen Voices API (V3 - paginated, filterable)
// Returns { data: HeygenVoice[], has_more: boolean, next_token: string | null }
export const listHeygenVoices = async (params?: {
  type?: 'public' | 'private';
  engine?: string;
  language?: string;
  gender?: 'male' | 'female';
  limit?: number;
  token?: string;
}) => {
  const response = await api.get('/api/heygen-voices', { params });
  return response.data;
};

// Fetch every voice by walking next_token until has_more is false.
// V3 caps limit at 100; default limit on the API is 20.
export const listAllHeygenVoices = async (filters?: {
  type?: 'public' | 'private';
  engine?: string;
  language?: string;
  gender?: 'male' | 'female';
}): Promise<{ data: HeygenVoice[] }> => {
  const all: HeygenVoice[] = [];
  let token: string | undefined;
  do {
    const page = await listHeygenVoices({ ...filters, limit: 100, token });
    if (Array.isArray(page?.data)) all.push(...page.data);
    token = page?.has_more ? page?.next_token ?? undefined : undefined;
  } while (token);
  return { data: all };
};

export const addHeygenVoice = async (workspaceId: string, actorId: string, voiceId: string, voiceName: string, previewUrl: string, videoPageId: string) => {
  const response = await api.post(`/api/heygen-voices/add-voice/${workspaceId}/${actorId}`, {voiceId, previewUrl, videoPageId, voiceName});
  return response.data;
};

export const removeHeygenVoice = async (workspaceId: string, actorId: string, videoPageId: string) => {
  const response = await api.delete(`/api/heygen-voices/add-voice/${workspaceId}/${videoPageId}/${actorId}`);
  return response.data;
};

// Voice Design API (HeyGen V3)

// Design a voice from a natural language prompt. Returns up to 3 matching voices.
// Saves results server-side, grouped under one generationId. Increment seed for a different batch.
export const designVoice = async (params: {
  prompt: string;
  gender?: 'male' | 'female';
  locale?: string;
  seed?: number;
  workspaceId: string;
  userId: string;
}): Promise<{
  success: boolean;
  data: {
    generationId: string;
    seed: number;
    voices: CustomVoice[];
  };
}> => {
  const response = await api.post('/api/voice-design', params);
  return response.data;
};

// List all saved custom voices for a workspace
export const getCustomVoicesByWorkspace = async (
  workspaceId: string
): Promise<{ success: boolean; data: CustomVoice[] }> => {
  const response = await api.get(`/api/voice-design/workspace/${workspaceId}`);
  return response.data;
};

// Get all voices that came back from a single designVoice call
export const getCustomVoicesByGenerationId = async (
  generationId: string
): Promise<{ success: boolean; data: CustomVoice[] }> => {
  const response = await api.get(`/api/voice-design/generation/${generationId}`);
  return response.data;
};

// Delete a saved custom voice
export const deleteCustomVoice = async (
  id: string
): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete(`/api/voice-design/${id}`);
  return response.data;
};

// Synthesize speech from text (HeyGen V3 /voices/speech).
// The voice must support the starfish engine — use listHeygenVoices({ engine: 'starfish' }).
export const generateSpeech = async (params: {
  text: string;
  voice_id: string;
  input_type?: 'text' | 'ssml';
  speed?: number;
  language?: string;
  locale?: string;
}): Promise<{
  success: boolean;
  data: {
    audio_url: string;
    duration: number;
    request_id: string | null;
    word_timestamps: Array<{ word: string; start: number; end: number }> | null;
  };
}> => {
  const response = await api.post('/api/voice-design/speech', params);
  return response.data;
};

export const elevenLabsFF = async () => {
  const response = await api.get('/api/feature-flip/eleven-labs');
  return response.data;
};

export const testVideoFF = async (email: string) => {
  const response = await api.get(`/api/feature-flip/test-videos/${email}`);
  return response.data;
}


// Creative Request API
export const createCreativeRequest = async (data: object) => {
  try {
    const response = await api.post('/api/creative-requests', data);
    return response.data;
  } catch (error: any) {
    throw new Error(`Error creating creative request: ${error.response?.data?.message || error.message}`);
  }
};

export const getCreativeRequestById = async (id: string) => {
  try {
    const response = await api.get(`/api/creative-requests/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(`Error fetching creative request: ${error.response?.data?.message || error.message}`);
  }
};

export const getAllCreativeRequests = async () => {
  try {
    const response = await api.get('/api/creative-requests');
    return response.data;
  } catch (error: any) {
    throw new Error(`Error fetching all creative requests: ${error.response?.data?.message || error.message}`);
  }
};

export const updateCreativeRequest = async (id: string, data: object) => {
  try {
    const response = await api.put(`/api/creative-requests/${id}`, data);
    return response.data;
  } catch (error: any) {
    throw new Error(`Error updating creative request: ${error.response?.data?.message || error.message}`);
  }
};

export const deleteCreativeRequest = async (id: string) => {
  try {
    const response = await api.delete(`/api/creative-requests/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(`Error deleting creative request: ${error.response?.data?.message || error.message}`);
  }
};

export const getAvailableWorkspacesForCreativeRequest = async (requesterId: string, receiverId: string) => {
  try {
    const response = await api.get(`/api/creative-requests/available-workspaces/${requesterId}/${receiverId}`);
    return response.data.workspaces;
  } catch (error: any) {
    throw new Error(`Error fetching available workspaces: ${error.response?.data?.message || error.message}`);
  }
};

export const checkEmailWhitelist = async (email: string): Promise<boolean> => {
  try {
    const response = await api.get(`/api/auth/whitelist/${encodeURIComponent(email)}`);
    return response.data.isWhitelisted;
  } catch (error) {
    console.error('Error checking email whitelist:', error);
    return false;
  }
};

// Plans api
/**
 * Get all available plans
 * @returns Promise<Array<Plan>> - List of all available plans
 */
// Plans API
export const getAllPlans = async (): Promise<any[]> => {
  try {
    const response = await api.get('/api/plans');
    return response.data;
  } catch (error) {
    console.error('Error fetching plans:', error);
    throw error;
  }
};

export const getPlanById = async (planId: string): Promise<any> => {
  try {
    const response = await api.get(`/api/plans/${planId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching plan:', error);
    throw error;
  }
};

export const createPlan = async (planData: {
  name: string;
  videoLimit: number;
  seats: number;
  pricePerUGC: number;
  features?: string[];
}): Promise<any> => {
  try {
    const response = await api.post('/api/plans', planData);
    return response.data;
  } catch (error) {
    console.error('Error creating plan:', error);
    throw error;
  }
};

export const updatePlan = async (planId: string, planData: {
  name?: string;
  videoLimit?: number;
  seats?: number;
  pricePerUGC?: number;
  features?: string[];
}): Promise<any> => {
  try {
    const response = await api.put(`/api/plans/${planId}`, planData);
    return response.data;
  } catch (error) {
    console.error('Error updating plan:', error);
    throw error;
  }
};

export const deletePlan = async (planId: string): Promise<void> => {
  try {
    await api.delete(`/api/plans/${planId}`);
  } catch (error) {
    console.error('Error deleting plan:', error);
    throw error;
  }
};

// Features API
export const getAllFeatures = async (): Promise<any[]> => {
  try {
    const response = await api.get('/api/features');
    return response.data;
  } catch (error) {
    console.error('Error fetching features:', error);
    throw error;
  }
};

export const getFeatureById = async (featureId: string): Promise<any> => {
  try {
    const response = await api.get(`/api/features/${featureId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching feature:', error);
    throw error;
  }
};

/**
 * Check if workspace has video credits remaining
 * @param workspaceId - ID of the workspace to check
 * @returns Promise<boolean> - True if workspace has video credits remaining
 */
export const checkVideoCredits = async (workspaceId: string): Promise<boolean> => {
  try {
    const response = await api.get(`/api/plans/${workspaceId}/video-credits`);
    return response.data.hasCredits;
  } catch (error) {
    console.error('Error checking video credits:', error);
    throw error;
  }
};

/**
 * Check if workspace has available seats
 * @param workspaceId - ID of the workspace to check
 * @returns Promise<boolean> - True if workspace has available seats
 */
export const checkSeats = async (workspaceId: string): Promise<boolean> => {
  try {
    const response = await api.get(`/api/plans/${workspaceId}/seats`);
    return response.data.hasSeats;
  } catch (error) {
    console.error('Error checking available seats:', error);
    throw error;
  }
};

/**
 * Upgrade workspace to a new plan
 * @param workspaceId - ID of workspace to upgrade
 * @param planName - Name of plan to upgrade to
 * @returns Promise<Workspace> - Updated workspace object
 */
export const upgradeWorkspacePlan = async (
  workspaceId: string,
  planName: string
): Promise<any> => {
  try {
    const response = await api.put(`/api/plans/${workspaceId}/upgrade`, { planName });
    return response.data;
  } catch (error) {
    console.error('Error upgrading workspace plan:', error);
    throw error;
  }
};

// Admin - Organizations
export const createOrganization = async (organizationData: {
  name: string;
  organizationKey: string;
  letsLaunchAccess?: boolean;
}) => {
  const response = await api.post('/api/admin/organizations', organizationData);
  return response.data;
};

export const getAllOrganizations = async () => {
  const response = await api.get('/api/admin/organizations');
  return response.data;
};

// Admin - Users
export const createUser = async (userData: {
  name: string;
  email: string;
  password: string;
  organizationId: string;
  role?: string;
}) => {
  const response = await api.post('/api/admin/users', userData);
  return response.data;
};

export const getAllUsers = async (filters?: AdminUserFilters) => {
  const params = new URLSearchParams();
  if (filters?.search) params.append('search', filters.search);
  if (filters?.premium) params.append('premium', 'true');
  if (filters?.organization) params.append('organization', filters.organization);
  if (filters?.role) params.append('role', filters.role);
  
  const response = await api.get(`/api/admin/users?${params.toString()}`);
  return response.data;
};

export const grantFullAccess = async (
  userId: string,
  expiresAt?: Date,
  trialCredits?: number,
  trialCreditsVideoAgent?: number,
  trialCreditsPromptToVideo?: number,
) => {
  const response = await api.post(`/api/admin/users/${userId}/full-access`, {
    expiresAt,
    trialCredits,
    trialCreditsVideoAgent,
    trialCreditsPromptToVideo,
  });
  return response.data;
};

// Admin - Premium Analytics
export const getPremiumUsers = async () => {
  const response = await api.get('/api/admin/premium/users');
  return response.data;
};

export const getPremiumWorkspaces = async (filters?: AdminWorkspaceFilters) => {
  const params = new URLSearchParams();
  if (filters?.search) params.append('search', filters.search);
  if (filters?.hasPlan) params.append('hasPlan', 'true');
  if (filters?.isTrial) params.append('isTrial', 'true');
  if (filters?.organization) params.append('organization', filters.organization);
  
  const response = await api.get(`/api/admin/premium/workspaces?${params.toString()}`);
  return response.data;
};

// Admin - Stats
export const getAdminStats = async () => {
  const response = await api.get('/api/admin/stats');
  return response.data;
};

// Admin - Template Analytics
export interface TemplateAnalytics {
  template_id: string;
  template_name: string;
  times_used: number;
  createdAt: string;
  updatedAt: string;
}

export const getTemplateAnalytics = async (): Promise<TemplateAnalytics[]> => {
  const response = await api.get('/api/admin/analytics/templates');
  return response.data;
};

export const verifyAdmin = async (): Promise<boolean> => {
  try {
    const response = await api.get('/api/admin/verify-admin');
    return response.data.isAdmin;
  } catch (error) {
    console.error('Error verifying admin status:', error);
    return false;
  }
};

export const updateUser = async (userId: string, userData: Partial<User>) => {
  const response = await api.put(`/api/admin/users/${userId}`, userData);
  return response.data;
};

export const updateWorkspaceLimits = async (
  workspaceId: string,
  limits: {
    videoLimit?: number;
    heygenMonthlyLimit?: number;
    videoAgentMonthlyLimit?: number;
    promptToVideoMonthlyLimit?: number;
    avatarTotalLimit?: number;
  }
) => {
  const response = await api.put(`/api/admin/workspaces/${workspaceId}/limits`, limits);
  return response.data;
};

export const getBillingData = (billingType: string) => {
  return api.get(`/api/admin/billing?billingType=${billingType}`);
};

export const getMonthlyBilling = (month: number, year: number, billingType: string) => {
  return api.get(`/api/admin/billing/monthly?month=${month}&year=${year}&billingType=${billingType}`);
};

export const getUsageOverview = async (params?: {
  search?: string;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: string;
}) => {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.append('search', params.search);
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.limit) queryParams.append('limit', params.limit.toString());
  if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
  if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
  const query = queryParams.toString();
  const url = query ? `/api/admin/users/usage-overview?${query}` : '/api/admin/users/usage-overview';
  const response = await api.get(url);
  return response.data;
};

export const getUserUsageDetails = async (userId: string) => {
  const response = await api.get(`/api/admin/users/${userId}/usage-details`);
  return response.data;
};

export const createCustomPlan = async (planData: {
  pricePerUgc: number;
  videoLimit: number;
  seats?: number;
  description?: string;
}) => {
  const response = await api.post('/api/plans/custom', planData);
  return response.data;
};

export const addPlanToWorkspace = async (
  workspaceId: string,
  planData: {
    planType: string;
    customPlanData?: {
      pricePerUgc: number;
      videoLimit: number;
      seats?: number;
    };
  }
) => {
  const response = await api.post(`/api/admin/workspaces/${workspaceId}/plan`, planData);
  return response.data;
};

export const removePlanFromWorkspace = async (workspaceId: string) => {
  const response = await api.delete(`/api/admin/workspaces/${workspaceId}/plan`);
  return response.data;
};

export const updateWorkspacePlanAndCredits = async (
  workspaceId: string,
  data: {
    videoLimit?: number;
    trialCredits?: number;
    trialCreditsVideoAgent?: number;
    trialCreditsPromptToVideo?: number;
    pricePerUGC?: number;
  }
) => {
  const response = await api.put(
    `/api/admin/workspaces/${workspaceId}/plan-and-credits`,
    data
  );
  return response.data;
};

export const revokeFullAccess = async (userId: string) => {
  const response = await api.post(`/api/admin/users/${userId}/revoke-access`);
  return response.data;
};

export const uploadBackgroundAsset = async (
  file: File,
  userId: string,
  workspaceId: string,
  projectId?: string,
  videoPageId?: string
) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('userId', userId);
  formData.append('workspaceId', workspaceId);
  if (projectId) formData.append('projectId', projectId);
  if (videoPageId) formData.append('videoPageId', videoPageId);

  const response = await api.post('/api/assets/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const getAssetsByWorkspace = async (workspaceId: string) => {
  const response = await api.get(`/api/assets/workspace/${workspaceId}`);
  return response.data;
};

export const deleteAsset = async (assetId: string) => {
  const response = await api.delete(`/api/assets/${assetId}`);
  return response.data;
};

// V2 Asset Upload - supports both HeyGen (default) and S3
export const uploadBackgroundAssetV2 = async (
  file: File,
  userId: string,
  workspaceId: string,
  options?: {
    organizationId?: string;
    projectId?: string;
    videoPageId?: string;
    source?: 'heygen' | 's3';
  }
) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('userId', userId);
  formData.append('workspaceId', workspaceId);
  if (options?.organizationId) formData.append('organizationId', options.organizationId);
  if (options?.projectId) formData.append('projectId', options.projectId);
  if (options?.videoPageId) formData.append('videoPageId', options.videoPageId);
  if (options?.source) formData.append('source', options.source);

  const response = await api.post('/api/assets/v2/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

// Get assets by organization
export const getAssetsByOrganization = async (organizationId: string) => {
  const response = await api.get(`/api/assets/organization/${organizationId}`);
  return response.data;
};

export const updateActorReactionSettings = async (
  actorId: string,
  workspaceId: string,
  videoPageId: string,
  settings: {
    avatarScale?: number;
    backgroundAssetId?: string;
    avatarOffset?: {x: number, y: number};
    backgroundFit?: 'cover' | 'crop' | 'contain' | 'none';
    backgroundPlayStyle?: 'fit_to_scene' | 'freeze' | 'loop' | 'once';
  }
) => {
  const response = await api.put(
    `/api/actors/${workspaceId}/${videoPageId}/${actorId}/reaction-settings`,
    settings
  );
  return response.data;
};

export const renameGeneratedVideo = async (videoId: string, newName: string) => {
  const response = await api.patch(`/api/generated-videos/${videoId}/rename`, {
    newName
  });
  return response.data;
};

// Voice Settings API Methods
export const updateVoiceSettings = async (
  workspaceId: string,
  videoPageId: string,
  actorId: string,
  voiceSettings: TextVoiceSettings 
  | AudioVoiceSettings 
  | SilenceVoiceSettings
) => {
  try {
    const response = await api.put(
      `/api/actors/${workspaceId}/${videoPageId}/${actorId}/voice-settings`,
      { voice_settings: voiceSettings }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating voice settings:", error);
    throw error;
  }
};

export const updateElevenLabsVoice = async (
  workspaceId: string,
  videoPageId: string,
  actorId: string,
  voiceId: string,
  voiceName?: string
) => {
  try {
    const response = await api.put(
      `/api/actors/${workspaceId}/${videoPageId}/${actorId}/elevenlabs-voice`,
      { elevenLabsVoiceId: voiceId, voiceName }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating ElevenLabs voice:", error);
    throw error;
  }
};

export const updateHeygenVoice = async (
  workspaceId: string,
  videoPageId: string,
  actorId: string,
  voiceId: string,
  audioUrl?: string,
  heygenTemplate?: any
) => {
  try {
    const response = await api.put(
      `/api/actors/${workspaceId}/${videoPageId}/${actorId}/heygen-voice`,
      { heygenVoiceId: voiceId, heygenVoiceAudioUrl: audioUrl, heygenTemplate }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating Heygen voice:", error);
    throw error;
  }
};

// Actor Settings API Methods
export const getActorById = async (
  workspaceId: string,
  videoPageId: string,
  actorId: string
) => {
  try {
    const response = await api.get(
      `/api/actors/${workspaceId}/${videoPageId}/${actorId}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching actor:", error);
    throw error;
  }
};

export const updateActorSettings = async (
  workspaceId: string,
  videoPageId: string,
  actorId: string,
  settings: {
    avatarScale?: number;
    backgroundAssetId?: string;
    avatarOffset?: { x: number; y: number };
    caption?: boolean;
    title?: string;
    voice_settings?: TextVoiceSettings 
    | AudioVoiceSettings
    | SilenceVoiceSettings;
    elevenLabsVoiceId?: string;
    heygenVoiceId?: string;
    heygenVoiceAudioUrl?: string;
    isTalkingPhoto?: boolean;
    talking_photo_id?: string;
    talking_photo_name?: string;
  }
) => {
  try {
    const response = await api.put(
      `/api/actors/${workspaceId}/${videoPageId}/${actorId}/settings`,
      settings
    );
    return response.data;
  } catch (error) {
    console.error("Error updating actor settings:", error);
    throw error;
  }
};

export const getCaptionPresets = async (): Promise<{ presets: string[] }> => {
  const response = await api.get(`/api/generated-videos/caption-presets`);
  return response.data;
};

export const getVideoWithCaptions = async (videoId: string) => {
  const response = await api.get(`/api/generated-videos/${videoId}/with-captions`);
  return response.data;
};

export const downloadCaptions = async (
  videoId: string,
  format: 'ass' | 'srt' | 'txt' | 'svg'
): Promise<Blob> => {
  const response = await api.get(
    `/api/generated-videos/${videoId}/captions?format=${format}`,
    {
      responseType: 'blob'
    }
  );
  return response.data;
};

export const getVoiceLocales = async (): Promise<VoiceLocale[]> => {
  try {
    const response = await api.get('/api/actors/voices/locales');
    return response.data;
  } catch (error) {
    console.error("Error fetching voice locales:", error);
    throw error;
  }
};

// Template API Methods
export const getTemplates = async () => {
  const response = await api.get('/api/templates');
  return response.data;
};

export const getTemplateVariables = async (templateId: string) => {
  const response = await api.get(`/api/templates/${templateId}/variables`);
  return response.data;
};

export const applyTemplateToActor = async (
  actorId: string,
  templateData: {
    template_id: string;
    name: string;
    thumbnail_image_url?: string;
    aspect_ratio?: "portrait" | "landscape";
  },
  variables?: Record<string, any>
) => {
  const response = await api.post(`/api/templates/${actorId}/apply-template`, {
    templateData, // Send the full templateData object
  });
  return response.data;
};

export const updateActorTemplate = async (
  actorId: string,
  templateData: {
    template_id: string;
    variables?: Record<string, any>;
  }
) => {
  const response = await api.put(`/api/actors/${actorId}/template`, templateData);
  return response.data;
};

export const removeTemplateFromActor = async (actorId: string) => {
  const response = await api.delete(`/api/actors/${actorId}/template`);
  return response.data;
};

export const generateVideoWithTemplate = async (
  workspaceId: string,
  videoPageId: string,
  actorId: string,
  templateId: string,
  variables: Record<string, any>,
  dimension: { width: number; height: number },
  userEmail: string
) => {
  const response = await api.post(`/api/videopages/${videoPageId}/generate-template-video`, {
    workspaceId,
    actorId,
    templateId,
    variables,
    dimension,
    userEmail
  });
  return response.data;
};

export const applyTemplatesToActors = async (
  actorIds: string[],
  templateId: string,
  variables: any
) => {
  const response = await api.post('/api/templates/apply-templates', {
    actorIds,
    templateId,
    variables
  });
  return response.data;
};

export const registerTemplateActor = async (
  workspaceId: string,
  videoPageId: string,
  templateData: {
    template_id: string;
    name: string;
    thumbnail_image_url: string | undefined;
    aspect_ratio: 'portrait' | 'landscape' | undefined;
  },
  variables: Record<string, any> = {}, // New: variables parameter
  options?: {
    actorId?: string;
    caption?: boolean;
    title?: string;
  }
) => {
  const response = await api.post('/api/templates/add-template', {
    template_id: templateData.template_id,
    workspaceId,
    videoPageId,
    template_name: templateData.name,
    thumbnail_image_url: templateData.thumbnail_image_url,
    aspect_ratio: templateData.aspect_ratio,
    variables, // Include variables in request
    ...options
  });
  return response.data;
};

export const getTemplateGuidelines = async (templateId: string) => {
  const response = await api.get(`/api/templates/${templateId}/guidelines`);
  return response.data;
};


export const uploadVideoToGoogleDrive = async (accessToken: string, videoUrl: string, fileName: string, folderId: string | null = null) => {
  const response = await api.post('/api/google-drive/upload', {
    accessToken,
    videoUrl,
    fileName,
    folderId
  });
  return response.data;
};

export const uploadVideoToGoogleDriveWithService = async (videoUrl: string, fileName: string, folderId: string | null = null) => {
  const response = await api.post('/api/google-drive/upload-service', {
    videoUrl,
    fileName,
    folderId
  });
  return response.data;
};

export const createGoogleDriveFolder = async (accessToken: string, folderName: string, parentFolderId = null) => {
  const response = await api.post('/api/google-drive/create-folder', {
    accessToken,
    folderName,
    parentFolderId
  });
  return response.data;
};

export const getGoogleAccessToken = async () => {
  // This should return the Google access token from your auth system
  // You might need to modify your auth to store this
  return localStorage.getItem('google_access_token');
};


export const requestGoogleDriveAccess = async (userId: string) => {
  const response = await api.post('/api/google-drive/request-access', {
    userId
  });
  return response.data;
};

export const checkGoogleDriveAccess = async (userId: string) => {
  const response = await api.get(`/api/google-drive/check-access/${userId}`);
  return response.data;
};

export const markNotificationAsSeen = async (userId: string, notificationCode: string) => {
  const response = await api.post('/api/auth/mark-notification-seen', {
    userId,
    notificationCode
  });
  return response.data;
};

export const initiateGoogleDriveSignIn = (redirectPath?: string): void => {
  if (redirectPath) {
    localStorage.setItem('drive_redirect_path', redirectPath);
  }
  
  // Redirect to Google Drive OAuth endpoint
  window.location.href = `${process.env.REACT_APP_API_URL}/api/auth/google/drive`;
};

export const hasGoogleDriveScope = (): boolean => {
  const driveScopeGranted = localStorage.getItem('google_drive_scope_granted');
  const accessToken = localStorage.getItem('google_access_token');
  
  // Check if we have both the token and the scope flag
  return !!accessToken && driveScopeGranted === 'true';
};

export const setGoogleDriveScopeGranted = (): void => {
  localStorage.setItem('google_drive_scope_granted', 'true');
};

export const clearGoogleDriveScope = (): void => {
  localStorage.removeItem('google_drive_scope_granted');
};

export const updateGeneratedVideoWithDriveUrl = async (videoId: string, driveUrl: string) => {
  const response = await api.patch(`/api/generated-videos/${videoId}/drive-url`, {
    googleDriveUrl: driveUrl
  });
  return response.data;
};



// IV Avatar Video API Methods
export const generateIVAvatarVideo = async (data: GenerateIVAvatarVideoRequest): Promise<{
  videoId: string;
  ivAvatarVideoId: string;
  message: string;
}> => {
  const response = await api.post('/api/iv-avatar-videos/generate', data);
  return response.data;
};

// Update Heygen voice for Avatar Lab actor
export const updateAvatarLabHeygenVoice = async (
  workspaceId: string,
  avatarId: string,
  voiceId: string,
  previewAudioUrl: string
): Promise<void> => {
  const response = await api.put(`/api/iv-avatar-videos/${avatarId}/heygen-voice`, {
    workspaceId,
    voiceId,
    previewAudioUrl,
  });
  return response.data;
};

export const updateIVAvatarVideoStatus = async (videoId: string): Promise<IVAvatarVideo> => {
  const response = await api.get(`/api/iv-avatar-videos/${videoId}/status`);
  return response.data;
};

export const getIVAvatarVideoById = async (videoId: string): Promise<IVAvatarVideo> => {
  const response = await api.get(`/api/iv-avatar-videos/${videoId}`);
  return response.data;
};

export const deleteIVAvatarVideo = async (videoId: string): Promise<{ message: string }> => {
  const response = await api.delete(`/api/iv-avatar-videos/${videoId}`);
  return response.data;
};

export const getIVAvatarVideosByWorkspace = async (workspaceId: string): Promise<IVAvatarVideo[]> => {
  const response = await api.get(`/api/iv-avatar-videos/workspace/${workspaceId}`);
  return response.data;
};

// Custom Photo Avatar API Methods
export const generateCustomPhotoAvatar = async (data: GenerateCustomPhotoAvatarRequest): Promise<{
  generationId: string;
  customPhotoAvatarId: string;
  message: string;
}> => {
  const response = await api.post('/api/custom-photo-avatars/generate', data);
  return response.data;
};

export const checkPhotoAvatarStatus = async (generationId: string): Promise<{
  avatar: CustomPhotoAvatar;
  message: string;
}> => {
  const response = await api.get(`/api/custom-photo-avatars/generation/${generationId}/status`);
  return response.data;
};

export const selectPhotoAvatarImage = async (
  avatarId: string, 
  data: SelectPhotoAvatarImageRequest
): Promise<{
  avatar: CustomPhotoAvatar;
  message: string;
}> => {
  const response = await api.post(`/api/custom-photo-avatars/${avatarId}/select-image`, data);
  return response.data;
};

export const getPhotoAvatarDetails = async (photoAvatarId: string): Promise<{
  details: any;
  message: string;
}> => {
  const response = await api.get(`/api/custom-photo-avatars/details/${photoAvatarId}`);
  return response.data;
};

export const updatePhotoAvatarDetails = async (
  avatarId: string, 
  details: any
): Promise<{
  avatar: CustomPhotoAvatar;
  message: string;
}> => {
  const response = await api.put(`/api/custom-photo-avatars/${avatarId}/details`, details);
  return response.data;
};

export const updateCustomPhotoAvatarStatus = async (
  generationId: string, 
  data: {
    status: 'processing' | 'success' | 'failed';
    image_url_list?: string[];
    image_key_list?: string[];
    photo_avatar_id?: string;
    error_message?: string;
  }
): Promise<CustomPhotoAvatar> => {
  const response = await api.put(`/api/custom-photo-avatars/${generationId}/status`, data);
  return response.data;
};

export const getCustomPhotoAvatarByGenerationId = async (generationId: string): Promise<CustomPhotoAvatar> => {
  const response = await api.get(`/api/custom-photo-avatars/generation/${generationId}`);
  return response.data;
};

export const getCustomPhotoAvatarById = async (avatarId: string): Promise<CustomPhotoAvatar> => {
  const response = await api.get(`/api/custom-photo-avatars/${avatarId}`);
  return response.data;
};

export const deleteCustomPhotoAvatar = async (avatarId: string): Promise<{ message: string }> => {
  const response = await api.delete(`/api/custom-photo-avatars/${avatarId}`);
  return response.data;
};

// Update a custom photo avatar's display name and/or default voice id.
// Used by the Avatar Design page to rename a look or change its default voice.
export const updateAvatarLook = async (
  avatarId: string,
  payload: { name?: string; default_voice_id?: string }
): Promise<{ message: string; avatar: CustomPhotoAvatar }> => {
  const response = await api.patch(`/api/custom-photo-avatars/${avatarId}/look`, payload);
  return response.data;
};

export const getCustomPhotoAvatarsByWorkspace = async (workspaceId: string): Promise<CustomPhotoAvatar[]> => {
  const response = await api.get(`/api/custom-photo-avatars/workspace/${workspaceId}`);
  return response.data;
};

export const updateCustomPhotoAvatarVoiceSettings = async (
  avatarId: string, 
  data: UpdateCustomPhotoAvatarVoiceSettingsRequest
): Promise<CustomPhotoAvatar> => {
  const response = await api.put(`/api/custom-photo-avatars/${avatarId}/voice-settings`, data);
  return response.data;
};

export const generateIVAvatarVideosMultipleActors = async (
  workspaceId: string,
  videoPageId: string,
  avatarLabActors: CustomPhotoAvatar[],
  script: Script,
  dimension: { width: number, height: number },
  userEmail: string,
  isSpeech: boolean = false,
  speechAudioUrl?: string,
  resolution?: '4k' | '1080p' | '720p'
) => {
  const results = [];
  
  // Process actors sequentially to avoid STS conflicts
  for (const avatarLabActor of avatarLabActors) {
    try {
      const isV3 = avatarLabActor.apiVersion === 'v3';
      // V3 records send avatar_look_id (HeyGen look ID) and skip image_key (no two-step flow).
      // V2 records still rely on image_key from the legacy generate→selectImage flow.
      const imageKey = isV3
        ? undefined
        : avatarLabActor.selected_image_key || avatarLabActor.image_key_list[0];

      if (!isV3 && !imageKey) {
        throw new Error(`No image key available for avatar: ${avatarLabActor.name}`);
      }
      if (isV3 && !avatarLabActor.avatar_look_id) {
        throw new Error(`V3 avatar missing avatar_look_id: ${avatarLabActor.name}`);
      }

      const useElevenLabs = !!avatarLabActor.elevenLabsVoiceId;
      const voiceId = useElevenLabs ? avatarLabActor.elevenLabsVoiceId : avatarLabActor.heygenVoiceId;

      if (!voiceId) {
        throw new Error(`No voice selected for avatar: ${avatarLabActor.name}`);
      }

      const requestData: GenerateIVAvatarVideoRequest = {
        workspaceId,
        videoPageId,
        userEmail,
        image_key: imageKey || '',
        video_title: avatarLabActor.name + `  ${formatDate()}`,
        script: isSpeech ? '' : `${script.headline || ''}\n${script.body || ''}\n${script.cta || ''}`,
        voice_id: voiceId,
        video_orientation: dimension.height > dimension.width ? 'portrait' : 'landscape',
        fit: 'cover',
        dimension,
        ...(resolution && { resolution }),
        // V3: pass avatar_look_id for HeyGen V3 video generation (avatar_id field server-side)
        ...(isV3 && avatarLabActor.avatar_look_id && { avatar_look_id: avatarLabActor.avatar_look_id }),
        ...(isV3 && avatarLabActor.apiVersion && { apiVersion: avatarLabActor.apiVersion }),
        isSpeech,
        useElevenLabs,
        ...(useElevenLabs && {
          elevenLabsVoiceId: avatarLabActor.elevenLabsVoiceId,
          elevenLabsVoiceSettings: avatarLabActor.voiceConfiguration
        })
      };

      if (isSpeech && speechAudioUrl) {
        requestData.audio_url = speechAudioUrl;
      }

      const response = await generateIVAvatarVideo(requestData);
      results.push(response.videoId);

    } catch (error: any) {
      // Don't swallow silently — the call site renders "Generating 0 videos"
      // when every actor fails, which is opaque. Annotate the original error
      // with the actor name and rethrow so the caller can surface it.
      console.error(`Failed to generate video for ${avatarLabActor.name}:`, error);
      const reason = error?.response?.data?.message || error?.message || 'Unknown error';
      const err = new Error(`Failed to generate video for "${avatarLabActor.name}": ${reason}`);
      (err as any).cause = error;
      throw err;
    }
  }

  return results.filter(id => id !== null); // Return only successful video IDs
};

// Update Avatar Lab voice settings
export const updateAvatarLabVoiceSettings = async (
  avatarId: string,
  workspaceId: string,
  voiceSettings: TextVoiceSettings,
  heygenVoiceId?: string,
  heygenVoiceAudioUrl?: string
): Promise<CustomPhotoAvatar> => {
  const response = await api.put(`/api/custom-photo-avatars/${avatarId}/voice-settings`, {
    workspaceId,
    voiceSettings,
    heygenVoiceId,
    heygenVoiceAudioUrl
  });
  return response.data;
};

// Update video page with Avatar Lab actors
export const updateVideoPageAvatarLabActors = async (
  videoPageId: string,
  avatarLabActorIds: string[],
  userEmail: string,
  workspaceId: string
): Promise<VideoPage> => {
  const response = await api.put(`/api/videopages/${videoPageId}/avatar-lab-actors`, {
    avatarLabActorIds,
    userEmail,
    workspaceId
  });
  return response.data;
};

// Remove Avatar Lab actor from video page
export const removeVideoPageAvatarLabActor = async (
  videoPageId: string,
  avatarLabActorId: string
): Promise<VideoPage> => {
  const response = await api.delete(`/api/videopages/${videoPageId}/avatar-lab-actor`, {
    data: { avatarLabActorId }
  });
  return response.data;
};

export const renameIVAvatarVideo = async (videoId: string, name: string): Promise<IVAvatarVideo> => {
  const response = await api.put(`/api/iv-avatar-videos/${videoId}/rename`, { name });
  return response.data;
};

// Download IV Avatar Video
export const downloadIVAvatarVideo = async (videoId: string): Promise<{ downloadUrl: string }> => {
  const response = await api.get(`/api/iv-avatar-videos/${videoId}/download`);
  return response.data;
};

export const updateIVAvatarVideoWithDriveUrl = async (
  videoId: string, 
  driveUrl: string
): Promise<IVAvatarVideo> => {
  const response = await api.put(`/api/iv-avatar-videos/${videoId}/drive-url`, { driveUrl });
  return response.data;
};

export const generateIVAvatarVideosMultipleScripts = async (
  workspaceId: string,
  videoPageId: string,
  avatarLabActorId: string, // Single actor for variables
  scripts: Script[], // Array of scripts with variable replacements
  dimension: { width: number; height: number },
  userEmail: string,
  resolution?: '4k' | '1080p' | '720p',
) => {
  try {
    const response = await api.post(`/api/iv-avatar-videos/generate-multiple-scripts`, {
      workspaceId,
      videoPageId,
      avatarLabActorId,
      scripts,
      dimension,
      userEmail,
      ...(resolution && { resolution }),
    });
    return response.data.videoIds;
  } catch (error) {
    console.error("Error generating IV avatar videos:", error);
    throw error;
  }
};

export const getCustomPhotoAvatarsByOrganization = async (organizationId: string) => {
  try {
    const response = await api.get(`/api/custom-photo-avatars/organization/${organizationId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching custom photo avatars by organization:", error);
    throw error;
  }
};

// Upload asset and create custom photo avatar
export const uploadAssetAndCreateAvatar = async (data: {
  workspaceId: string;
  videoPageId?: string;
  userEmail: string;
  name: string;
  age?: string;
  gender?: string;
  ethnicity?: string;
  orientation?: string;
  pose?: string;
  style?: string;
  appearance?: string;
  image: File;
}) => {
  const formData = new FormData();
  
  // Append all form fields
  formData.append('workspaceId', data.workspaceId);
  if (data.videoPageId) formData.append('videoPageId', data.videoPageId);
  formData.append('userEmail', data.userEmail);
  formData.append('name', data.name);
  if (data.age) formData.append('age', data.age);
  if (data.gender) formData.append('gender', data.gender);
  if (data.ethnicity) formData.append('ethnicity', data.ethnicity);
  if (data.orientation) formData.append('orientation', data.orientation);
  if (data.pose) formData.append('pose', data.pose);
  if (data.style) formData.append('style', data.style);
  if (data.appearance) formData.append('appearance', data.appearance);
  
  // Append the image file
  formData.append('image', data.image);

  try {
    const response = await api.post('/api/custom-photo-avatars/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading asset and creating avatar:', error);
    throw error;
  }
};

// ============================================
// CustomPhotoAvatar V3 API (HeyGen V3)
// ============================================

const CUSTOM_PHOTO_AVATARS_V3_BASE = '/api/custom-photo-avatars/v3';

// Create a V3 photo avatar from an uploaded image
export const createCustomPhotoAvatarV3FromImage = async (data: {
  workspaceId: string;
  userEmail: string;
  name: string;
  videoPageId?: string;
  avatarGroupId?: string;
  image: File;
}): Promise<CreateCustomPhotoAvatarV3UploadResponse> => {
  const formData = new FormData();
  formData.append('workspaceId', data.workspaceId);
  formData.append('userEmail', data.userEmail);
  formData.append('name', data.name);
  if (data.videoPageId) formData.append('videoPageId', data.videoPageId);
  if (data.avatarGroupId) formData.append('avatarGroupId', data.avatarGroupId);
  formData.append('image', data.image);

  const response = await api.post(`${CUSTOM_PHOTO_AVATARS_V3_BASE}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

// Create a V3 AI-generated avatar from a text prompt
export const createCustomPhotoAvatarV3FromPrompt = async (
  data: CreateCustomPhotoAvatarV3PromptRequest
): Promise<CreateCustomPhotoAvatarV3UploadResponse> => {
  const response = await api.post(`${CUSTOM_PHOTO_AVATARS_V3_BASE}/generate`, data);
  return response.data;
};

// Create a V3 digital-twin avatar from a video
export const createCustomPhotoAvatarV3FromVideo = async (data: {
  workspaceId: string;
  userEmail: string;
  name: string;
  videoPageId?: string;
  avatarGroupId?: string;
  video: File;
}): Promise<CreateCustomPhotoAvatarV3UploadResponse> => {
  const formData = new FormData();
  formData.append('workspaceId', data.workspaceId);
  formData.append('userEmail', data.userEmail);
  formData.append('name', data.name);
  if (data.videoPageId) formData.append('videoPageId', data.videoPageId);
  if (data.avatarGroupId) formData.append('avatarGroupId', data.avatarGroupId);
  formData.append('video', data.video);

  const response = await api.post(`${CUSTOM_PHOTO_AVATARS_V3_BASE}/digital-twin`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

// Rename an avatar look (only photo_avatar / digital_twin types — prompt type returns 400)
export const updateCustomPhotoAvatarV3LookName = async (
  lookId: string,
  name: string
): Promise<UpdateAvatarLookV3Response> => {
  const response = await api.patch(`${CUSTOM_PHOTO_AVATARS_V3_BASE}/looks/${lookId}`, { name });
  return response.data;
};

// List avatar groups for the current organization (DB-backed, not the HeyGen proxy).
export const listCustomPhotoAvatarV3Groups = async (params: {
  organizationId: string;
  limit?: number;
  token?: string;
}): Promise<ListAvatarGroupsV3Response> => {
  const query = new URLSearchParams();
  query.append('organizationId', params.organizationId);
  if (params.limit) query.append('limit', String(params.limit));
  if (params.token) query.append('token', params.token);
  const response = await api.get(`${CUSTOM_PHOTO_AVATARS_V3_BASE}/groups?${query.toString()}`);
  return response.data;
};

// Get a specific avatar group (used for polling status of in-progress avatars)
export const getCustomPhotoAvatarV3Group = async (
  groupId: string
): Promise<GetAvatarGroupV3Response> => {
  const response = await api.get(`${CUSTOM_PHOTO_AVATARS_V3_BASE}/groups/${groupId}`);
  return response.data;
};

// Sync a single V3 look's status from HeyGen and persist to local DB.
// Use this (not the group endpoint) when polling a new look attached to an
// already-trained group — group status reports 'completed' immediately while
// the look itself is still processing.
export const syncCustomPhotoAvatarV3Look = async (
  lookId: string
): Promise<{ avatar: CustomPhotoAvatar; heygenStatus: string }> => {
  const response = await api.get(`${CUSTOM_PHOTO_AVATARS_V3_BASE}/looks/${lookId}/sync`);
  return response.data;
};

// Create custom photo avatar from URL (asset library)
export const createAvatarFromUrl = async (data: {
  workspaceId: string;
  videoPageId?: string;
  userEmail: string;
  name: string;
  age?: string;
  gender?: string;
  ethnicity?: string;
  orientation?: string;
  pose?: string;
  style?: string;
  appearance?: string;
  imageUrl: string;
}) => {
  try {
    const response = await api.post('/api/custom-photo-avatars/from-url', data);
    return response.data;
  } catch (error) {
    console.error('Error creating avatar from URL:', error);
    throw error;
  }
};

// Avatar Groups API Calls

/**
 * Create an avatar group from a custom photo avatar
 */
export const createAvatarGroup = async (data: AvatarGroupCreationRequest) => {
  try {
    const response = await api.post('/api/avatar-groups/create', data);
    return response.data;
  } catch (error) {
    console.error('Error creating avatar group:', error);
    throw error;
  }
};

/**
 * Check avatar group training status
 */
export const checkAvatarGroupTrainingStatus = async (groupId: string) => {
  try {
    const response = await api.get(`/api/avatar-groups/training-status/${groupId}`);
    return response.data;
  } catch (error) {
    console.error('Error checking avatar group training status:', error);
    throw error;
  }
};

/**
 * Generate a new look for an avatar group
 */
export const generateAvatarGroupLook = async (data: LookGenerationRequest) => {
  try {
    const response = await api.post('/api/avatar-groups/generate-look', data);
    return response.data;
  } catch (error) {
    console.error('Error generating avatar group look:', error);
    throw error;
  }
};

/**
 * Get avatar group by ID
 */
export const getAvatarGroup = async (groupId: string) => {
  try {
    const response = await api.get(`/api/avatar-groups/${groupId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting avatar group:', error);
    throw error;
  }
};

/**
 * Get avatar group by HeyGen group ID
 */
export const getAvatarGroupByGroupId = async (groupId: string) => {
  try {
    const response = await api.get(`/api/avatar-groups/by-group-id/${groupId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting avatar group by group ID:', error);
    throw error;
  }
};

/**
 * Get all avatar groups for a workspace
 */
export const getAvatarGroupsByWorkspace = async (workspaceId: string) => {
  try {
    const response = await api.get(`/api/avatar-groups/workspace/${workspaceId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting avatar groups by workspace:', error);
    throw error;
  }
};

/**
 * Update avatar group from HeyGen
 */
export const updateAvatarGroupFromHeyGen = async (groupId: string) => {
  try {
    const response = await api.put(`/api/avatar-groups/update-from-heygen/${groupId}`);
    return response.data;
  } catch (error) {
    console.error('Error updating avatar group from HeyGen:', error);
    throw error;
  }
};

/**
 * Check if an avatar can be used for group creation
 */
export const canCreateAvatarGroup = (avatar: CustomPhotoAvatar): boolean => {
  return (
    (avatar.status === 'success' || avatar.status === 'completed') &&
    (!!avatar.selected_image_key || !!avatar.generation_id || !!avatar.avatar_look_id)
  );
};

/**
 * Check if an avatar is part of a group
 */
export const isAvatarInGroup = (avatar: CustomPhotoAvatar): boolean => {
  return !!avatar.avatar_group || !!avatar.group_id;
};

/**
 * Check if an avatar is a base avatar for a group
 */
export const isBaseAvatar = (avatar: CustomPhotoAvatar): boolean => {
  return avatar.is_base_avatar === true;
};

// Utility functions for avatar groups

/**
 * Poll avatar group training status until ready or failed
 */
export const pollAvatarGroupTrainingStatus = async (
  groupId: string, 
  interval = 10000, // 10 seconds
  maxAttempts = 180 // 30 minutes total
): Promise<any> => {
  let attempts = 0;
  
  const checkStatus = async (): Promise<any> => {
    attempts++;
    try {
      const response = await checkAvatarGroupTrainingStatus(groupId);
      const avatarGroup = response.avatarGroup;
      
      if (avatarGroup.training_status === 'ready') {
        return avatarGroup;
      } else if (avatarGroup.training_status === 'failed') {
        throw new Error(`Avatar group training failed: ${avatarGroup.training_error_msg}`);
      } else if (attempts >= maxAttempts) {
        throw new Error('Avatar group training timeout');
      } else {
        // Still training, wait and check again
        await new Promise(resolve => setTimeout(resolve, interval));
        return checkStatus();
      }
    } catch (error) {
      console.error('Error in training status check:', error);
      throw error;
    }
  };
  
  return checkStatus();
};
/**
 * Poll look generation status until complete or failed
 */
export const pollLookGenerationStatus = async (
  generationId: string,
  interval = 5000,
  maxAttempts = 60
): Promise<CustomPhotoAvatar> => {
  let attempts = 0;
  
  const checkStatus = async (): Promise<CustomPhotoAvatar> => {
    attempts++;
    const response = await checkPhotoAvatarStatus(generationId);
    const avatar = response.avatar;
    
    if (avatar.status === 'success') {
      return avatar;
    } else if (avatar.status === 'failed') {
      throw new Error(`Look generation failed: ${avatar.error_message}`);
    } else if (attempts >= maxAttempts) {
      throw new Error('Look generation timeout');
    } else {
      // Still processing, wait and check again
      await new Promise(resolve => setTimeout(resolve, interval));
      return checkStatus();
    }
  };
  
  return checkStatus();
};

/**
 * Get all avatar groups for an organization
 */
export const getAvatarGroupsByOrganization = async (organizationId: string) => {
  try {
    const response = await api.get(`/api/avatar-groups/organization/${organizationId}`);
    return response.data;
  } catch (error) {
    console.error('Error getting avatar groups by organization:', error);
    throw error;
  }
};

export const startAvatarGroupTraining = async (data: { groupId: string; workspaceId: string; userEmail: string }) => {
  try {
    const response = await api.post('/api/avatar-groups/start-training', data);
    return response.data;
  } catch (error) {
    console.error('Error starting avatar group training:', error);
    throw error;
  }
};

export const deleteAvatarGroup = async (groupId: string) => {
  const response = await api.delete(`/api/avatar-groups/${groupId}`);
  return response.data;
};

// Set the default voice for an entire avatar group. The backend fans the voice
// out to each look (HeyGen PATCH /v3/avatars/looks/{look_id}) and mirrors it
// onto the group + every look in our DB so video generation can reuse it.
export const updateAvatarGroupDefaultVoice = async (
  groupId: string,
  defaultVoiceId: string | null
): Promise<{
  avatarGroup: AvatarGroup;
  syncFailures: Array<{ lookId: string; error: unknown }>;
  message: string;
}> => {
  const response = await api.patch(`/api/avatar-groups/${groupId}/default-voice`, {
    default_voice_id: defaultVoiceId,
  });
  return response.data;
};

export const updateCustomAvatarElevenLabsVoice = async (avatarId: string, data: any) => {
  const response = await api.put(`/api/custom-photo-avatars/${avatarId}/elevenlabs-voice`, data);
  return response.data;
};

// Add motion to a custom photo avatar
// Returns baseAvatar (unchanged) and motionAvatar (new avatar with motion)
export const addMotionToAvatar = async (
  avatarId: string,
  data: {
    motion_type: 'expressive' | 'consistent' | 'consistent_gen_3' | 'hailuo_2' | 'veo2' | 'seedance_lite' | 'kling';
    prompt?: string;
  }
): Promise<{
  baseAvatar: CustomPhotoAvatar;
  motionAvatar: CustomPhotoAvatar | null; // null if still processing
  motionId: string;
  groupId: string;
  message: string;
}> => {
  const response = await api.post(`/api/custom-photo-avatars/${avatarId}/add-motion`, data);
  return response.data;
};

// Add sound effect to a custom photo avatar (requires motion to be added first)
export const addSoundEffectToAvatar = async (
  avatarId: string
): Promise<{
  avatar: CustomPhotoAvatar;
  soundEffectId: string;
  message: string;
}> => {
  const response = await api.post(`/api/custom-photo-avatars/${avatarId}/add-sound-effect`);
  return response.data;
};

// Generate voice preview audio with selected voice and settings
export const generateVoicePreview = async (data: {
  voice_id: string;
  text: string;
  text_type?: string;
  settings?: {
    pitch?: string;
    speed?: string;
    volume?: string;
  };
}): Promise<{
  data: {
    audio_url: string;
    duration: number;
    auto_engine: string | null;
    is_pass: boolean;
    job_id: string | null;
    word_timestamps: Array<{ word: string; start: number; end: number }>;
  };
  message: string;
}> => {
  const response = await api.post('/api/custom-photo-avatars/voice-preview', data);
  return response.data;
};

// Sync motion preview URL for a motion avatar
// Use this to poll for the motion_preview_url after adding motion
export const syncMotionPreviewUrl = async (
  avatarId: string
): Promise<{
  avatar: CustomPhotoAvatar;
  motionPreviewUrl: string | null;
  message: string;
}> => {
  const response = await api.post('/api/custom-photo-avatars/sync-motion-details', {
    avatarId,
  });
  return response.data;
};

// Voice Suggestions API

// Voice suggestion type
export interface VoiceSuggestion {
  voice_id: string;
  voice_name: string;
  audio_preview_url: string;
}

export interface VoiceSuggestionResponse {
  heygen_actor_id: string;
  actor_name: string;
  voice_suggestions: VoiceSuggestion[];
}

// Get all voice suggestions
export const getAllVoiceSuggestions = async (): Promise<VoiceSuggestionResponse[]> => {
  const response = await api.get('/api/voice-suggestions');
  return response.data;
};

// Get voice suggestions for a specific actor
// If no suggestions exist and actorName is provided, it auto-generates suggestions
export const getVoiceSuggestionsForActor = async (
  actorId: string,
  actorName?: string
): Promise<VoiceSuggestionResponse> => {
  const params = new URLSearchParams();
  if (actorName) params.append('actorName', actorName);

  const response = await api.get(`/api/voice-suggestions/actor/${actorId}?${params.toString()}`);
  // API returns { success: true, data: { ... } }, so we need to access response.data.data
  return response.data.data;
};

// Add a voice suggestion for an actor
export const addVoiceSuggestion = async (
  actorId: string,
  actorName: string,
  voiceId: string
): Promise<VoiceSuggestionResponse> => {
  const response = await api.post(`/api/voice-suggestions/actor/${actorId}/voice`, {
    actorName,
    voiceId,
  });
  return response.data;
};

// Remove a voice suggestion from an actor
export const removeVoiceSuggestion = async (
  actorId: string,
  voiceId: string
): Promise<VoiceSuggestionResponse> => {
  const response = await api.delete(`/api/voice-suggestions/actor/${actorId}/voice/${voiceId}`);
  return response.data;
};

// Update actor name for voice suggestions
export const updateVoiceSuggestionActorName = async (
  actorId: string,
  actorName: string
): Promise<VoiceSuggestionResponse> => {
  const response = await api.put(`/api/voice-suggestions/actor/${actorId}`, {
    actorName,
  });
  return response.data;
};

// Delete all voice suggestions for an actor
export const deleteAllVoiceSuggestionsForActor = async (actorId: string): Promise<void> => {
  await api.delete(`/api/voice-suggestions/actor/${actorId}`);
};

// ============================================
// Sora2 Media Generation API
// ============================================

const SORA2_BASE_URL = '/api/sora2';

// Create text-to-video task
export const createSora2TextToVideo = async (
  data: CreateTextToVideoRequest
): Promise<Sora2Response> => {
  const response = await api.post(`${SORA2_BASE_URL}/text-to-video`, data);
  return response.data;
};

// Create image-to-video task
export const createSora2ImageToVideo = async (
  data: CreateImageToVideoRequest
): Promise<Sora2Response> => {
  const response = await api.post(`${SORA2_BASE_URL}/image-to-video`, data);
  return response.data;
};

// Create pro text-to-video task
export const createSora2ProTextToVideo = async (
  data: CreateProTextToVideoRequest
): Promise<Sora2Response> => {
  const response = await api.post(`${SORA2_BASE_URL}/pro/text-to-video`, data);
  return response.data;
};

// Create pro image-to-video task
export const createSora2ProImageToVideo = async (
  data: CreateProImageToVideoRequest
): Promise<Sora2Response> => {
  const response = await api.post(`${SORA2_BASE_URL}/pro/image-to-video`, data);
  return response.data;
};

// Create storyboard task
export const createSora2Storyboard = async (
  data: CreateStoryboardRequest
): Promise<Sora2Response> => {
  const response = await api.post(`${SORA2_BASE_URL}/storyboard`, data);
  return response.data;
};

// Get task by ID
export const getSora2Task = async (taskId: string): Promise<Sora2TaskResponse> => {
  const response = await api.get(`${SORA2_BASE_URL}/task/${taskId}`);
  return response.data;
};

// Get all tasks (with optional filters)
export const getSora2Tasks = async (filters?: {
  workspaceId?: string;
  userId?: string;
  state?: Sora2State;
}): Promise<Sora2TasksResponse> => {
  const params = new URLSearchParams();
  if (filters?.workspaceId) params.append('workspaceId', filters.workspaceId);
  if (filters?.userId) params.append('userId', filters.userId);
  if (filters?.state) params.append('state', filters.state);

  const queryString = params.toString();
  const url = queryString ? `${SORA2_BASE_URL}/tasks?${queryString}` : `${SORA2_BASE_URL}/tasks`;

  const response = await api.get(url);
  return response.data;
};

// Delete task
export const deleteSora2Task = async (
  taskId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete(`${SORA2_BASE_URL}/task/${taskId}`);
  return response.data;
};

// Save generated media to workspace assets
export const saveSora2ToAssets = async (
  data: SaveToAssetsRequest
): Promise<SaveToAssetsResponse> => {
  const response = await api.post(`${SORA2_BASE_URL}/save-to-assets`, data);
  return response.data;
};

// Get prompt-to-media asset count for workspace
export const getPromptToMediaCount = async (
  workspaceId: string
): Promise<{ success: boolean; data: { count: number } }> => {
  const response = await api.get(`${SORA2_BASE_URL}/workspace/${workspaceId}/prompt-to-media-count`);
  return response.data;
};

// Remove watermark from generated media
export const removeSora2Watermark = async (
  data: { taskId: string; workspaceId: string }
): Promise<{ success: boolean; message: string; data: any }> => {
  const response = await api.post(`${SORA2_BASE_URL}/remove-watermark`, data);
  return response.data;
};

// Remove media from workspace
export const removeSora2MediaFromWorkspace = async (
  workspaceId: string,
  mediaId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete(`${SORA2_BASE_URL}/workspace/${workspaceId}/media/${mediaId}`);
  return response.data;
};

// ============================================
// Seedance (ByteDance Seedance V1 Pro) API
// ============================================

const SEEDANCE_BASE_URL = '/api/seedance';

// Create text-to-video task
export const createSeedanceTextToVideo = async (
  data: CreateSeedanceTextToVideoRequest
): Promise<SeedanceResponse> => {
  const response = await api.post(`${SEEDANCE_BASE_URL}/text-to-video`, data);
  return response.data;
};

// Create image-to-video task
export const createSeedanceImageToVideo = async (
  data: CreateSeedanceImageToVideoRequest
): Promise<SeedanceResponse> => {
  const response = await api.post(`${SEEDANCE_BASE_URL}/image-to-video`, data);
  return response.data;
};

// Create Seedance 1.5 Pro task (unified endpoint, optional images)
export const createSeedance15Pro = async (
  data: CreateSeedance15ProRequest
): Promise<SeedanceResponse> => {
  const response = await api.post(`${SEEDANCE_BASE_URL}/1.5-pro`, data);
  return response.data;
};

// Create Seedance 2.0 task (text-to-video, image-to-video, or multimodal reference)
export const createSeedance2 = async (
  data: CreateSeedance2Request
): Promise<SeedanceResponse> => {
  const response = await api.post(`${SEEDANCE_BASE_URL}/2.0`, data);
  return response.data;
};

// Create Seedance 2.0 Fast task (text-to-video or multimodal reference; no first/last frame)
export const createSeedance2Fast = async (
  data: CreateSeedance2FastRequest
): Promise<SeedanceResponse> => {
  const response = await api.post(`${SEEDANCE_BASE_URL}/2.0-fast`, data);
  return response.data;
};

// Create Seedance 2.5 task (text-to-video, image-to-video, or multimodal reference)
export const createSeedance25 = async (
  data: CreateSeedance25Request
): Promise<SeedanceResponse> => {
  const response = await api.post(`${SEEDANCE_BASE_URL}/2.5`, data);
  return response.data;
};

// Get task by ID
export const getSeedanceTask = async (taskId: string): Promise<SeedanceTaskResponse> => {
  const response = await api.get(`${SEEDANCE_BASE_URL}/task/${taskId}`);
  return response.data;
};

// Get all tasks (with optional filters)
export const getSeedanceTasks = async (filters?: {
  workspaceId?: string;
  userId?: string;
  state?: SeedanceState;
}): Promise<SeedanceTasksResponse> => {
  const params = new URLSearchParams();
  if (filters?.workspaceId) params.append('workspaceId', filters.workspaceId);
  if (filters?.userId) params.append('userId', filters.userId);
  if (filters?.state) params.append('state', filters.state);

  const queryString = params.toString();
  const url = queryString ? `${SEEDANCE_BASE_URL}/tasks?${queryString}` : `${SEEDANCE_BASE_URL}/tasks`;

  const response = await api.get(url);
  return response.data;
};

// Save generated media to workspace assets
export const saveSeedanceToAssets = async (
  data: SaveToAssetsRequest
): Promise<SaveToAssetsResponse> => {
  const response = await api.post(`${SEEDANCE_BASE_URL}/save-to-assets`, data);
  return response.data;
};

// Delete task
export const deleteSeedanceTask = async (
  taskId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete(`${SEEDANCE_BASE_URL}/task/${taskId}`);
  return response.data;
};

// Remove media from workspace
export const removeSeedanceMediaFromWorkspace = async (
  workspaceId: string,
  mediaId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete(`${SEEDANCE_BASE_URL}/workspace/${workspaceId}/media/${mediaId}`);
  return response.data;
};

// ============================================
// Nano Banana (kie.ai Nano Banana 2 - prompt to image) API
// ============================================

const NANO_BANANA_BASE_URL = '/api/nano-banana';

// Create Nano Banana image generation task
export const createNanoBanana = async (
  data: CreateNanoBananaRequest
): Promise<NanoBananaResponse> => {
  const response = await api.post(`${NANO_BANANA_BASE_URL}/generate`, data);
  return response.data;
};

// Get task by ID
export const getNanoBananaTask = async (taskId: string): Promise<NanoBananaTaskResponse> => {
  const response = await api.get(`${NANO_BANANA_BASE_URL}/task/${taskId}`);
  return response.data;
};

// Get all tasks (with optional filters)
export const getNanoBananaTasks = async (filters?: {
  workspaceId?: string;
  userId?: string;
  state?: NanoBananaState;
}): Promise<NanoBananaTasksResponse> => {
  const params = new URLSearchParams();
  if (filters?.workspaceId) params.append('workspaceId', filters.workspaceId);
  if (filters?.userId) params.append('userId', filters.userId);
  if (filters?.state) params.append('state', filters.state);

  const queryString = params.toString();
  const url = queryString ? `${NANO_BANANA_BASE_URL}/tasks?${queryString}` : `${NANO_BANANA_BASE_URL}/tasks`;

  const response = await api.get(url);
  return response.data;
};

// Save generated media to workspace assets
export const saveNanoBananaToAssets = async (
  data: SaveToAssetsRequest
): Promise<SaveToAssetsResponse> => {
  const response = await api.post(`${NANO_BANANA_BASE_URL}/save-to-assets`, data);
  return response.data;
};

// Delete task
export const deleteNanoBananaTask = async (
  taskId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete(`${NANO_BANANA_BASE_URL}/task/${taskId}`);
  return response.data;
};

// Remove media from workspace
export const removeNanoBananaMediaFromWorkspace = async (
  workspaceId: string,
  mediaId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete(`${NANO_BANANA_BASE_URL}/workspace/${workspaceId}/media/${mediaId}`);
  return response.data;
};

// ============================================
// Gemini (Google Gemini Omni 1.1 Flash) API
// ============================================

const GEMINI_BASE_URL = '/api/gemini';

// Create Gemini generation task
export const createGeminiGenerate = async (
  data: CreateGeminiGenerateRequest
): Promise<GeminiResponse> => {
  const response = await api.post(`${GEMINI_BASE_URL}/generate`, data);
  return response.data;
};

// Get task by ID
export const getGeminiTask = async (taskId: string): Promise<GeminiTaskResponse> => {
  const response = await api.get(`${GEMINI_BASE_URL}/task/${taskId}`);
  return response.data;
};

// Get all tasks (with optional filters)
export const getGeminiTasks = async (filters?: {
  workspaceId?: string;
  userId?: string;
  state?: GeminiState;
}): Promise<GeminiTasksResponse> => {
  const params = new URLSearchParams();
  if (filters?.workspaceId) params.append('workspaceId', filters.workspaceId);
  if (filters?.userId) params.append('userId', filters.userId);
  if (filters?.state) params.append('state', filters.state);

  const queryString = params.toString();
  const url = queryString ? `${GEMINI_BASE_URL}/tasks?${queryString}` : `${GEMINI_BASE_URL}/tasks`;

  const response = await api.get(url);
  return response.data;
};

// Save generated media to workspace assets
export const saveGeminiToAssets = async (
  data: SaveToAssetsRequest
): Promise<SaveToAssetsResponse> => {
  const response = await api.post(`${GEMINI_BASE_URL}/save-to-assets`, data);
  return response.data;
};

// Remove media from workspace
export const removeGeminiMediaFromWorkspace = async (
  workspaceId: string,
  mediaId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete(`${GEMINI_BASE_URL}/workspace/${workspaceId}/media/${mediaId}`);
  return response.data;
};

// ============================================
// Wan 3.0 Video API
// ============================================

const WAN3_BASE_URL = '/api/wan3';

// Create Wan 3.0 generation task
export const createWan3Generate = async (
  data: CreateWan3GenerateRequest
): Promise<Wan3Response> => {
  const response = await api.post(`${WAN3_BASE_URL}/generate`, data);
  return response.data;
};

// Create Wan 3.0 Prime generation task (same request/response shape as base Wan3,
// pricier/higher-quality tier — model id 'wan/3-0-video-prime')
export const createWan3PrimeGenerate = async (
  data: CreateWan3GenerateRequest
): Promise<Wan3Response> => {
  const response = await api.post(`${WAN3_BASE_URL}/prime/generate`, data);
  return response.data;
};

// Get task by ID
export const getWan3Task = async (taskId: string): Promise<Wan3TaskResponse> => {
  const response = await api.get(`${WAN3_BASE_URL}/task/${taskId}`);
  return response.data;
};

// Get all tasks (with optional filters)
export const getWan3Tasks = async (filters?: {
  workspaceId?: string;
  userId?: string;
  state?: Wan3State;
}): Promise<Wan3TasksResponse> => {
  const params = new URLSearchParams();
  if (filters?.workspaceId) params.append('workspaceId', filters.workspaceId);
  if (filters?.userId) params.append('userId', filters.userId);
  if (filters?.state) params.append('state', filters.state);

  const queryString = params.toString();
  const url = queryString ? `${WAN3_BASE_URL}/tasks?${queryString}` : `${WAN3_BASE_URL}/tasks`;

  const response = await api.get(url);
  return response.data;
};

// Save generated media to workspace assets
export const saveWan3ToAssets = async (
  data: SaveToAssetsRequest
): Promise<SaveToAssetsResponse> => {
  const response = await api.post(`${WAN3_BASE_URL}/save-to-assets`, data);
  return response.data;
};

// Remove media from workspace
export const removeWan3MediaFromWorkspace = async (
  workspaceId: string,
  mediaId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete(`${WAN3_BASE_URL}/workspace/${workspaceId}/media/${mediaId}`);
  return response.data;
};

// ============================================
// Hetzner Local LLM Engine API (self-hosted MiniMax H3)
// ============================================

const HETZNER_BASE_URL = '/api/hetzner-video';

export const createHetznerGenerate = async (
  data: CreateHetznerGenerateRequest
): Promise<HetznerResponse> => {
  const response = await api.post(`${HETZNER_BASE_URL}/generate`, data);
  return response.data;
};

export const getHetznerTask = async (taskId: string): Promise<HetznerTaskResponse> => {
  const response = await api.get(`${HETZNER_BASE_URL}/task/${taskId}`);
  return response.data;
};

export const getHetznerTasks = async (filters?: {
  workspaceId?: string;
  userId?: string;
  state?: HetznerState;
}): Promise<HetznerTasksResponse> => {
  const params = new URLSearchParams();
  if (filters?.workspaceId) params.append('workspaceId', filters.workspaceId);
  if (filters?.userId) params.append('userId', filters.userId);
  if (filters?.state) params.append('state', filters.state);

  const queryString = params.toString();
  const url = queryString ? `${HETZNER_BASE_URL}/tasks?${queryString}` : `${HETZNER_BASE_URL}/tasks`;

  const response = await api.get(url);
  return response.data;
};

export const saveHetznerToAssets = async (
  data: SaveToAssetsRequest
): Promise<SaveToAssetsResponse> => {
  const response = await api.post(`${HETZNER_BASE_URL}/save-to-assets`, data);
  return response.data;
};

export const removeHetznerMediaFromWorkspace = async (
  workspaceId: string,
  mediaId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete(`${HETZNER_BASE_URL}/workspace/${workspaceId}/media/${mediaId}`);
  return response.data;
};

// ============================================
// Prompt Suggestion API (claude-worker, trained on winning ads)
// ============================================

const PROMPT_SUGGESTION_BASE_URL = '/api/prompt-suggestions';

export const createPromptSuggestion = async (
  data: CreatePromptSuggestionRequest
): Promise<CreatePromptSuggestionResponse> => {
  const response = await api.post(`${PROMPT_SUGGESTION_BASE_URL}/generate`, data);
  return response.data;
};

export const getPromptSuggestionResult = async (
  jobId: string
): Promise<PromptSuggestionResultResponse> => {
  const response = await api.get(`${PROMPT_SUGGESTION_BASE_URL}/${jobId}`);
  return response.data;
};

// ─── Feedback Loop API ───────────────────────────────────────────────────────
const FEEDBACK_LOOP_BASE_URL = '/api/feedback-loop';

export const getFeedbackLoopMedia = async (workspaceId?: string): Promise<{ success: boolean; data: FeedbackLoopMedia[] }> => {
  const url = workspaceId ? `${FEEDBACK_LOOP_BASE_URL}/media?workspaceId=${workspaceId}` : `${FEEDBACK_LOOP_BASE_URL}/media`;
  return (await api.get(url)).data;
};

export const approveFeedbackLoopMedia = async (engine: string, id: string): Promise<{ success: boolean; data: FeedbackLoopMedia }> =>
  (await api.post(`${FEEDBACK_LOOP_BASE_URL}/media/${engine}/${id}/approve`)).data;

export const disapproveFeedbackLoopMedia = async (engine: string, id: string): Promise<{ success: boolean; message: string }> =>
  (await api.post(`${FEEDBACK_LOOP_BASE_URL}/media/${engine}/${id}/disapprove`)).data;

// ─── Submagic Caption API ────────────────────────────────────────────────────

export const getSubmagicTemplates = async (): Promise<{ templates: string[] }> => {
  const response = await api.get('/api/submagic/templates');
  return response.data;
};

export const getSubmagicLanguages = async (): Promise<{ languages: Array<{ name: string; code: string }> }> => {
  const response = await api.get('/api/submagic/languages');
  return response.data;
};

export const generateSubmagicCaptions = async (
  videoId: string,
  options: { templateName?: string; language?: string } = {}
): Promise<{ submagicProjectId: string; status: string; templateName: string }> => {
  const response = await api.post(`/api/submagic/generate/${videoId}`, options);
  return response.data;
};

export const getSubmagicProject = async (
  videoId: string
): Promise<{
  submagicProjectId: string;
  status: string;
  downloadUrl: string | null;
  directUrl: string | null;
  previewUrl: string | null;
  templateName: string;
  transcriptionStatus: string;
  hasWords: boolean;
  failureReason: string | null;
}> => {
  const response = await api.get(`/api/submagic/project/${videoId}`);
  return response.data;
};

export const exportSubmagicVideo = async (
  videoId: string
): Promise<{ status: string; message: string }> => {
  const response = await api.post(`/api/submagic/export/${videoId}`);
  return response.data;
};

export const downloadSubmagicCaptions = async (
  videoId: string,
  format: 'srt' | 'txt' = 'srt'
): Promise<Blob> => {
  const response = await api.get(`/api/submagic/captions/${videoId}?format=${format}`, {
    responseType: 'blob',
  });
  return response.data;
};
// ============================================
// Veo3 (Google Veo 3.1) API
// ============================================

const VEO3_BASE_URL = '/api/veo3';

// Generate video (text-to-video or image-to-video)
export const createVeo3Generate = async (
  data: CreateVeo3GenerateRequest
): Promise<Veo3Response> => {
  const response = await api.post(`${VEO3_BASE_URL}/generate`, data);
  return response.data;
};

// Extend a video
export const createVeo3Extend = async (
  data: CreateVeo3ExtendRequest
): Promise<Veo3ExtendResponse> => {
  const response = await api.post(`${VEO3_BASE_URL}/extend`, data);
  return response.data;
};

// Get 1080p version (instant, cached)
export const getVeo31080p = async (
  taskId: string
): Promise<Veo31080pResponse> => {
  const response = await api.get(`${VEO3_BASE_URL}/1080p/${taskId}`);
  return response.data;
};

// Start 4K upscaling (async)
export const startVeo3FourK = async (
  data: Veo3FourKRequest
): Promise<Veo3FourKResponse> => {
  const response = await api.post(`${VEO3_BASE_URL}/4k`, data);
  return response.data;
};

// Get task by ID
export const getVeo3Task = async (taskId: string): Promise<Veo3TaskResponse> => {
  const response = await api.get(`${VEO3_BASE_URL}/task/${taskId}`);
  return response.data;
};

// Get all tasks (with optional filters)
export const getVeo3Tasks = async (filters?: {
  workspaceId?: string;
  userId?: string;
  state?: Veo3State;
}): Promise<Veo3TasksResponse> => {
  const params = new URLSearchParams();
  if (filters?.workspaceId) params.append('workspaceId', filters.workspaceId);
  if (filters?.userId) params.append('userId', filters.userId);
  if (filters?.state) params.append('state', filters.state);

  const queryString = params.toString();
  const url = queryString ? `${VEO3_BASE_URL}/tasks?${queryString}` : `${VEO3_BASE_URL}/tasks`;

  const response = await api.get(url);
  return response.data;
};

// Delete task
export const deleteVeo3Task = async (
  taskId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete(`${VEO3_BASE_URL}/task/${taskId}`);
  return response.data;
};

// Save to asset library
export const saveVeo3ToAssets = async (
  data: SaveToAssetsRequest
): Promise<SaveToAssetsResponse> => {
  const response = await api.post(`${VEO3_BASE_URL}/save-to-assets`, data);
  return response.data;
};

// Remove media from workspace
export const removeVeo3MediaFromWorkspace = async (
  workspaceId: string,
  mediaId: string
): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete(`${VEO3_BASE_URL}/workspace/${workspaceId}/media/${mediaId}`);
  return response.data;
};

// ─── Extract Last Frame ────────────────────────────────────────────────────────

export const extractLastFrame = async (data: {
  videoUrl: string;
  workspaceId: string;
  userId: string;
  organizationId?: string;
}): Promise<{ url: string }> => {
  const response = await api.post('/api/prompt-to-video/extract-last-frame', data);
  return response.data;
};
